import { useState, useCallback, useMemo, useEffect, useRef, lazy, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import Header from "@/components/Header";
import Sidebar from "@/components/Sidebar";
import { usePermissions, useUser } from "@/hooks/useAuthV2";
import { useToast } from "@/hooks/use-toast";
import DrawingPanel from "@/components/DrawingPanel";
import { OsmImportDialog } from "@/components/OsmImportDialog";
import { OsmReviewPanel } from "@/components/OsmReviewPanel";
import { CadastreSearchPanel } from "@/components/CadastreSearchPanel";
import {
  GeometryCoordsImportDialog,
  type GeometryCoordsImportSuccess,
} from "@/components/GeometryCoordsImportDialog";
import MapToolbar from "@/components/MapToolbar";
import { OiMapRoutingBridge } from "@/components/map/OiMapRoutingBridge";
import { inferDrawToolFromFeature } from "@/lib/geometry-coords-import";
import Analytics from "@/pages/analytics";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { MAP_PANEL_Z } from "@/lib/map-panel-z";
import { cn } from "@/lib/utils";
import ObjectForm from "@/components/ObjectForm";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Geometry, GeoObject, GeometryAnalyzeResponse } from "@/types/api-v2";
import type { CardTemplateField, ObjectCreateInitialGeometryPayload } from "@/types/card-template";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Map as MapIcon, BarChart3, Table2, Copy } from "lucide-react";
import type {
  MeasureHudCursor,
  MeasureMode,
  MeasurePointResult,
} from "@/components/map-libre/MapLibreMeasureTool";
import type { ContourSelectMode } from "@/components/map-libre/MapLibreContourSelectTool";
import type { Feature, Polygon } from "geojson";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import MapFeatureContextMenu, {
  type MapFeatureContextTarget,
} from "@/components/map-libre/MapFeatureContextMenu";
import MapObjectsSplitTable from "@/components/MapObjectsSplitTable";
import { GeographicalFilterToolbar } from "@/components/GeographicalFilterSheet";
import {
  objectsApi,
  organizationsApi,
  objectTypesApi,
  geometriesApi,
  usersApi,
  spatialApi,
} from "@/services/api-v2.service";
import { klsSectionsApi } from "@/services/kls-api.service";
import { objectCardApi } from "@/services/card-api.service";
import { MapUiContext, type MapUiContextType, type SidebarTab, type MvtFilters, type MvtLayerSettings } from "@/contexts/MapUiContext";
import {
  mergeIdsIntoList as mergeMultiIds,
  normalizeObjectIdList,
  toggleIdInList,
} from "@/lib/map-multi-select";
import type { ReferenceLayerUiState } from "@/types/reference-layer";
import MainWorkspaceLayout from "@/components/workspace/MainWorkspaceLayout";
import MapDrawingModeBanner from "@/components/map-libre/MapDrawingModeBanner";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { hasToken } from "@/lib/api-client-v2";
import { objectQueryKeys } from "@/lib/query-keys";
import { useGeographyFilter } from "@/contexts/GeographyFilterContext";
import {
  mapPrimitiveCodeToDrawTool,
  resolveDrawToolForPrimitive,
} from "@/lib/map-geometry-tools";
import { convertCircleToPolygon } from "@/lib/utils";
import { spatialConfigToUserHints } from "@/lib/spatial-config-hints";
import { buildInitialGeometryPayloadForWithCard } from "@/lib/map-first-geometry-payload";
import { extractSelectionPolygonRing } from "@/lib/osm-selection-polygon";
import { envFeatureFlags } from "@/lib/feature-flags";
import { useModuleFlags } from "@/contexts/ModuleFlagsContext";
import { workItemsApi } from "@/services/work-items.service";
import type { WorkItemMapPoint, WorkItemMetaOption } from "@/types/work-items";
import {
  readMapLayersPersisted,
  writeMapLayersPersisted,
  DEFAULT_MVT_FILTERS,
  DEFAULT_MVT_LAYER_SETTINGS,
} from "@/lib/map-ui-layers-persist";
import {
  buildOiMapGridFeatureCollection,
  loadOiMapGridPrefs,
  saveOiMapGridPrefs,
  OI_MAP_GRID_LAYER,
  OI_MAP_GRID_SOURCE,
  type OiMapGridPrefs,
} from "@/lib/oi-map-grid";
import {
  loadOiNomPrefs,
  enabledOiNomScaleList,
  OI_NOM_PREFS_EVENT,
  loadOiNomPanelPrefs,
  saveOiNomPanelPrefs,
  OI_NOM_SOURCE,
  OI_NOM_FILL_LAYER,
  OI_NOM_LINE_LAYER,
  OI_NOM_LABEL_LAYER,
  OI_NOM_HIT_SOURCE,
  OI_NOM_HIT_FILL,
  OI_NOM_HIT_LINE,
} from "@/lib/oi-nomenclature-sheets";
import { nomenclatureSheetsApi } from "@/services/nomenclature-sheets-api.service";
import type { NomenclatureSheetHit } from "@/services/nomenclature-sheets-api.service";
import { OiNomenclatureIntersectPanel } from "@/components/oi/OiNomenclatureIntersectPanel";
import type { Map as MapLibreMapInstance } from "maplibre-gl";

const MapLibreMap = lazy(() => import("@/components/map-libre/MapLibreMap"));
const QUAL_ID_GEOMETRY_SEMANTIC_TYPES = 345;
const QUAL_ID_GEOMETRY_PRIMITIVE_TYPES = 343;

type MapWorkItemDraft = {
  kind: string;
  title: string;
  description: string;
  status: string;
  priority: number;
  assignedToUserId: string;
};

const MAP_WORK_ITEM_PRIORITY_OPTIONS = [1, 2, 3, 4, 5] as const;

export default function Home() {
  return (
    <HomeWithState />
  );
}

function HomeWithState() {
  const { data: user } = useUser();
  const { data: permissions = [] } = usePermissions();
  const { flags } = useModuleFlags();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const isAuthenticated = hasToken();
  const hasPermission = useCallback(
    (permission: string) => permissions.includes("admin.access") || permissions.includes(permission),
    [permissions],
  );

  const initialMapLayers = useMemo(() => {
    const p = readMapLayersPersisted();
    return {
      activeTool: p.activeTool ?? null,
      cadastralLayersVisible: p.cadastralLayersVisible ?? false,
      cadastralOpacity: p.cadastralOpacity ?? 0.7,
      telegramIncidentsVisible: p.telegramIncidentsVisible ?? false,
      telegramOpacity: p.telegramOpacity ?? 0.8,
      /**
       * `workItemsVisible: false` раньше попадал в localStorage с первого эффекта без клика по тумблеру.
       * Только `workItemsUserTouched: true` означает явный выбор; иначе снова берём feature flag (иначе GET /work-items/map никогда не уходит).
       */
      workItemsUserTouched: p.workItemsUserTouched === true,
      workItemsVisible:
        p.workItemsUserTouched === true ? p.workItemsVisible === true : envFeatureFlags.workItemsUiEnabled,
      workItemsOpacity: p.workItemsOpacity ?? 0.8,
      showWorkItemCreateButton: p.showWorkItemCreateButton ?? envFeatureFlags.workItemsUiEnabled,
      showBaseLayerSwitcher: p.showBaseLayerSwitcher ?? true,
      showMapExportButton: p.showMapExportButton ?? false,
      mvtFilters: { ...DEFAULT_MVT_FILTERS, ...(p.mvtFilters ?? {}) },
      mvtLayerSettings: { ...DEFAULT_MVT_LAYER_SETTINGS, ...(p.mvtLayerSettings ?? {}) },
    };
  }, []);

  // --- UI State ---
  const [selectedObject, setSelectedObject] = useState<GeoObject | null>(null);
  const selectedObjectRef = useRef<GeoObject | null>(null);
  selectedObjectRef.current = selectedObject;
  const [activeTool, setActiveTool] = useState<string | null>(() => initialMapLayers.activeTool);
  const [measureMode, setMeasureMode] = useState<MeasureMode>("line");
  const [measureLabel, setMeasureLabel] = useState<string | null>(null);
  const [measureHudCursor, setMeasureHudCursor] = useState<MeasureHudCursor | null>(null);
  const [measurePoint, setMeasurePoint] = useState<MeasurePointResult | null>(null);
  const [contourSelectMode, setContourSelectMode] = useState<ContourSelectMode>("polygon");
  const [contourFilter, setContourFilter] = useState<{
    objectIds: number[];
    feature: Feature<Polygon>;
  } | null>(null);
  const CONTOUR_INTERSECT_LIMIT = 1000;
  const [isDrawing, setIsDrawing] = useState(false);
  /** Dirty чертежа/правки из DrawingPanel (Wave 1.1 — баннер + beforeunload внутри панели). */
  const [drawingDirty, setDrawingDirty] = useState(false);
  useEffect(() => {
    if (!isDrawing) setDrawingDirty(false);
  }, [isDrawing]);
  /** Dirty runtime-карточки в ObjectPanel. */
  const [cardDirty, setCardDirty] = useState(false);
  const cardDirtyRef = useRef(false);
  cardDirtyRef.current = cardDirty;
  const [cardLeaveDialog, setCardLeaveDialog] = useState<{ run: () => void } | null>(null);
  /** Сброс кэша MVT в MapLibre после сохранения геометрии (см. mvtTilesEpoch в MapLibreMap). */
  const [mvtTilesEpoch, setMvtTilesEpoch] = useState(0);
  const [currentDrawing, setCurrentDrawing] = useState<any>(null);
  const [osmImportOpen, setOsmImportOpen] = useState(false);
  const [osmImportCoords, setOsmImportCoords] = useState<number[][]>([]);
  const [osmReviewBatchId, setOsmReviewBatchId] = useState<string | null>(null);
  const [cadastreSearchOpen, setCadastreSearchOpen] = useState(false);
  const [cadastreSearchCollapsed, setCadastreSearchCollapsed] = useState(false);
  const [originalEditingDrawing, setOriginalEditingDrawing] = useState<any>(null);
  const [currentObjectId, setCurrentObjectId] = useState<number | null>(null);
  const [isObjectFormOpen, setIsObjectFormOpen] = useState(false);
  const [isEditObjectFormOpen, setIsEditObjectFormOpen] = useState(false);
  const [objectToEdit, setObjectToEdit] = useState<GeoObject | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [mapInstance, setMapInstance] = useState<MapLibreMapInstance | null>(null);
  const [oiMapGridPrefs, setOiMapGridPrefs] = useState<OiMapGridPrefs>(() => loadOiMapGridPrefs());
  const [oiNomPanelOpen, setOiNomPanelOpen] = useState(
    () => loadOiNomPanelPrefs().open,
  );
  const setOiMapGridPrefsPersist = useCallback((next: OiMapGridPrefs) => {
    setOiMapGridPrefs(next);
    saveOiMapGridPrefs(next);
  }, []);
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>("objects");
  const sidebarTabRef = useRef(sidebarTab);
  sidebarTabRef.current = sidebarTab;
  const [activeView, setActiveView] = useState("map");
  const [selectedGeometry, setSelectedGeometry] = useState<Geometry | null>(null);
  const [focusedGeometryId, setFocusedGeometryId] = useState<number | null>(null);
  /** Multi-select карта ↔ Sidebar bulk (Wave 1.3). */
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [multiSelectedObjectIds, setMultiSelectedObjectIdsState] = useState<number[]>([]);
  /** Split карта+таблица (Wave 1.4). */
  const [mapSplitView, setMapSplitView] = useState(false);
  const [featureContextMenu, setFeatureContextMenu] = useState<MapFeatureContextTarget | null>(
    null,
  );
  const [cadastralLayersVisible, setCadastralLayersVisible] = useState(
    () => initialMapLayers.cadastralLayersVisible,
  );
  const [cadastralOpacity, setCadastralOpacity] = useState(() => initialMapLayers.cadastralOpacity);
  const [telegramIncidentsVisible, setTelegramIncidentsVisible] = useState(
    () => initialMapLayers.telegramIncidentsVisible,
  );
  const [telegramOpacity, setTelegramOpacity] = useState(() => initialMapLayers.telegramOpacity);
  const [workItemsUserTouched, setWorkItemsUserTouched] = useState(() => initialMapLayers.workItemsUserTouched);
  const [workItemsVisible, setWorkItemsVisible] = useState(() => initialMapLayers.workItemsVisible);
  const [workItemsOpacity, setWorkItemsOpacity] = useState(() => initialMapLayers.workItemsOpacity);
  const [showWorkItemCreateButton, setShowWorkItemCreateButton] = useState(
    () => initialMapLayers.showWorkItemCreateButton,
  );
  const [workItemsRefreshEpoch, setWorkItemsRefreshEpoch] = useState(0);
  const [workItemsPinnedPoints, setWorkItemsPinnedPoints] = useState<WorkItemMapPoint[]>([]);
  const [isMapWorkItemDialogOpen, setIsMapWorkItemDialogOpen] = useState(false);
  const [mapWorkItemAnchor, setMapWorkItemAnchor] = useState<{ lng: number; lat: number } | null>(null);
  const [mapWorkItemAutoLink, setMapWorkItemAutoLink] = useState<{
    objectId?: number;
    geometryId?: number;
    objectName?: string;
  } | null>(null);
  const [mapWorkItemSubmitting, setMapWorkItemSubmitting] = useState(false);
  const [mapWorkItemDraft, setMapWorkItemDraft] = useState<MapWorkItemDraft>({
    kind: "incident",
    title: "",
    description: "",
    status: "new",
    priority: 3,
    assignedToUserId: "",
  });
  const [hasShownObjectTypesWarning, setHasShownObjectTypesWarning] = useState(false);
  const [mapSearchQuery, setMapSearchQuery] = useState<string | null>(null);
  const [showBaseLayerSwitcher, setShowBaseLayerSwitcher] = useState<boolean>(
    () => initialMapLayers.showBaseLayerSwitcher,
  );
  const [showMapExportButton, setShowMapExportButton] = useState<boolean>(() => initialMapLayers.showMapExportButton);
  const [mvtFilters, setMvtFilters] = useState<MvtFilters>(() => initialMapLayers.mvtFilters);
  const [debouncedMvtFilters, setDebouncedMvtFilters] = useState<MvtFilters>(() => initialMapLayers.mvtFilters);
  const [mvtLayerSettings, setMvtLayerSettings] = useState<MvtLayerSettings>(() => initialMapLayers.mvtLayerSettings);
  const [referenceLayerUi, setReferenceLayerUi] = useState<Record<number, ReferenceLayerUiState>>({});
  const setReferenceLayerEnabled = useCallback(
    (layerId: number, enabled: boolean, defaultOpacity = 0.85) => {
      setReferenceLayerUi((prev) => {
        const cur = prev[layerId];
        return {
          ...prev,
          [layerId]: {
            enabled,
            opacity: cur?.opacity ?? defaultOpacity,
          },
        };
      });
    },
    [],
  );
  const setReferenceLayerOpacity = useCallback((layerId: number, opacity: number) => {
    setReferenceLayerUi((prev) => {
      const cur = prev[layerId];
      return {
        ...prev,
        [layerId]: {
          enabled: cur?.enabled ?? false,
          opacity,
        },
      };
    });
  }, []);
  const [isGeometryTypeDialogOpen, setIsGeometryTypeDialogOpen] = useState(false);
  const [coordsImportOpen, setCoordsImportOpen] = useState(false);
  /** Импорт координат: Feature ждёт выбора KLS-типов, затем уходит в DrawingPanel / map-first. */
  const pendingImportFeatureRef = useRef<GeometryCoordsImportSuccess | null>(null);
  const [pendingImportFeatureCount, setPendingImportFeatureCount] = useState<number | null>(null);
  /** Рисование без выбранного объекта: после анализа пользователь выбирает объект или создаёт новый. */
  const [mapFirstGeometryFlow, setMapFirstGeometryFlow] = useState(false);
  const [geometryAnalyzeResult, setGeometryAnalyzeResult] = useState<GeometryAnalyzeResponse | null>(null);
  const [isGeometryAnalyzeDialogOpen, setIsGeometryAnalyzeDialogOpen] = useState(false);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const mapFirstAnalyzeSuggested = useMemo(() => {
    const r = geometryAnalyzeResult;
    if (!r?.suggested_primary_object_id) return null;
    const c = r.candidates.find((x) => x.object_id === r.suggested_primary_object_id);
    if (!c?.can_attach_geometry) return null;
    return { candidate: c, reason: r.suggestion_reason };
  }, [geometryAnalyzeResult]);
  const [pendingDrawTool, setPendingDrawTool] = useState<string | null>(null);
  const [pendingDrawObjectId, setPendingDrawObjectId] = useState<number | null>(null);
  /** Контур для POST /objects/with-card (initial_geometry), если пользователь выбрал «Создать новый объект» из анализа. */
  const [initialGeometryForObjectForm, setInitialGeometryForObjectForm] =
    useState<ObjectCreateInitialGeometryPayload | null>(null);
  const [selectedGeometrySemanticKlsId, setSelectedGeometrySemanticKlsId] = useState<string | undefined>(undefined);
  const [selectedGeometryPrimitiveKlsId, setSelectedGeometryPrimitiveKlsId] = useState<string | undefined>(undefined);
  /** Синхронно с onValueChange Combobox: до commit state ref уже актуален (важно внутри Dialog + Popover/cmdk). */
  const selectedGeometrySemanticRef = useRef<string | undefined>(undefined);
  const selectedGeometryPrimitiveRef = useRef<string | undefined>(undefined);
  const confirmGeometryTypesRef = useRef<() => void>(() => {});
  /**
   * Выбор из списка с openSidebarDetails: false откладывает objectId в URL (setTimeout 0).
   * Ref = id целевого объекта: пока query не догнал выбор, эффект по URL не применяет старый objectId.
   */
  const listSelectionWithoutDetailsObjectIdRef = useRef<number | null>(null);
  /** Отложенная запись objectId в URL (setTimeout 0); отменять при снятии выделения, иначе query перезапишется и состояние разъедется. */
  const objectIdToUrlTimeoutRef = useRef<number | null>(null);
  /** Актуальный чертёж для колбэка после создания объекта (избегаем устаревшего замыкания). */
  const currentDrawingRef = useRef(currentDrawing);
  useEffect(() => {
    currentDrawingRef.current = currentDrawing;
  }, [currentDrawing]);

  /** Синхронизация с DrawingPanel: имя/описание/стиль для initial_geometry. */
  const mapFirstSnapshotRef = useRef({
    name: "",
    description: "",
    color: "#3B82F6",
    opacity: 50,
  });

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      setDebouncedMvtFilters(mvtFilters);
    }, 300);
    return () => window.clearTimeout(timerId);
  }, [mvtFilters]);

  /** Вкладка «Слои»: восстановление после F5 (localStorage). */
  useEffect(() => {
    writeMapLayersPersisted({
      activeTool,
      cadastralLayersVisible,
      cadastralOpacity,
      telegramIncidentsVisible,
      telegramOpacity,
      workItemsUserTouched,
      workItemsVisible,
      workItemsOpacity,
      showWorkItemCreateButton,
      showBaseLayerSwitcher,
      showMapExportButton,
      mvtFilters,
      mvtLayerSettings,
    });
  }, [
    activeTool,
    cadastralLayersVisible,
    cadastralOpacity,
    telegramIncidentsVisible,
    telegramOpacity,
    workItemsUserTouched,
    workItemsVisible,
    workItemsOpacity,
    showWorkItemCreateButton,
    showBaseLayerSwitcher,
    showMapExportButton,
    mvtFilters,
    mvtLayerSettings,
  ]);

  useEffect(() => {
    if (isMobile) setIsSidebarCollapsed(true);
  }, [isMobile]);

  const safeApiCall = useCallback(
    async <T,>(action: () => Promise<T>, fallbackValue: T, context: string, { silent = false } = {}) => {
      try {
        return await action();
      } catch (error) {
        console.error(`[Home] ${context}`, error);
        if (!silent) {
          toast({
            title: "Не удалось загрузить данные",
            description: context,
            variant: "destructive",
          });
        }
        return fallbackValue;
      }
    },
    [toast],
  );

  const canViewAnalytics = () => {
    if (!user) return false;
    return hasPermission("analytics.view") || hasPermission("admin.access");
  };

  // --- Data Queries ---
  const { territories, combine, journalSync } = useGeographyFilter();
  const geographyIdsKey = territories.map((item) => item.id).join(",");
  const journalUsesTerritory = journalSync && geographyIdsKey.length > 0;

  const { data: objects = [], isLoading: objectsLoading } = useQuery({
    queryKey: ["objects", journalUsesTerritory ? geographyIdsKey : "", journalUsesTerritory ? combine : ""],
    queryFn: () =>
      safeApiCall(
        () =>
          objectsApi.list(
            journalUsesTerritory
              ? { geography_ids: geographyIdsKey, geography_combine: combine, limit: 1000 }
              : undefined,
          ),
        [],
        "Проверьте подключение к бэкенду FastAPI (GET /api/v1/objects)",
      ),
    enabled: isAuthenticated,
  });

  const urlObjectIdToHydrate = useMemo(() => {
    const oidStr = searchParams.get("objectId");
    if (!oidStr) return null;
    const n = Number.parseInt(oidStr, 10);
    return Number.isNaN(n) ? null : n;
  }, [searchParams]);

  /**
   * Deep link ?objectId= с модуля РД: объект может отсутствовать в первой странице GET /objects.
   * Подгружаем карточку и добавляем в кэш списка, чтобы сработал выбор и автоподгон карты.
   */
  useEffect(() => {
    if (!isAuthenticated || urlObjectIdToHydrate == null || objectsLoading) return;
    if (objects.some((o) => Number(o.id) === urlObjectIdToHydrate)) return;

    let cancelled = false;
    void (async () => {
      try {
        const full = await objectsApi.get(urlObjectIdToHydrate);
        if (cancelled) return;
        queryClient.setQueryData<GeoObject[]>(
          ["objects", journalUsesTerritory ? geographyIdsKey : "", journalUsesTerritory ? combine : ""],
          (prev) => {
          const p = prev ?? [];
          if (p.some((o) => Number(o.id) === full.id)) return p;
          return [...p, full as GeoObject];
        });
      } catch {
        /* нет доступа или объект не найден */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, urlObjectIdToHydrate, objects, objectsLoading, queryClient, journalUsesTerritory, geographyIdsKey, combine]);

  const { data: objectTypes = [] } = useQuery({
    queryKey: ["object-types"],
    queryFn: async () => {
      const data = await safeApiCall(
        () => objectTypesApi.list(),
        [],
        "Типы объектов пока недоступны (GET /api/v1/object-types)",
        { silent: hasShownObjectTypesWarning },
      );
      if (data.length === 0 && !hasShownObjectTypesWarning) {
        setHasShownObjectTypesWarning(true);
      }
      return data;
    },
    enabled: isAuthenticated,
    retry: false,
  });

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations"],
    queryFn: () =>
      safeApiCall(
        () => organizationsApi.list(),
        [],
        "Проверьте подключение к бэкенду (GET /api/v1/organizations)",
      ),
    enabled: isAuthenticated,
  });
  const { data: mapWorkItemAssignees = [] } = useQuery({
    queryKey: ["work-items", "assignees", "home"],
    queryFn: () =>
      safeApiCall(
        () => usersApi.list({ limit: 1000 }),
        [],
        "Не удалось загрузить список исполнителей",
        { silent: true },
      ),
    enabled: isAuthenticated && flags.workItemsUiEnabled,
    staleTime: 60_000,
  });
  const { data: mapWorkItemMeta } = useQuery({
    queryKey: ["work-items", "meta", "home"],
    queryFn: () =>
      safeApiCall(
        () => workItemsApi.getMeta(),
        { kinds: [] as WorkItemMetaOption[], statuses: [] as WorkItemMetaOption[] },
        "Не удалось загрузить справочники типов и статусов задач",
        { silent: true },
      ),
    enabled: isAuthenticated && flags.workItemsUiEnabled,
    staleTime: 60_000,
  });
  const mapWorkItemKindOptions = useMemo(() => mapWorkItemMeta?.kinds ?? [], [mapWorkItemMeta?.kinds]);
  const mapWorkItemStatusOptions = useMemo(() => mapWorkItemMeta?.statuses ?? [], [mapWorkItemMeta?.statuses]);
  const mapWorkItemAssigneeOptions = useMemo<ComboboxOption[]>(() => {
    return mapWorkItemAssignees.map((u) => {
      const display = [u.last_name, u.first_name].filter(Boolean).join(" ").trim() || u.username || u.email || u.id;
      const meta = [u.username, u.email].filter(Boolean).join(" • ");
      return {
        value: u.id,
        label: `${display}${meta ? ` (${meta})` : ""}`,
        shortLabel: display,
        fullLabel: `${display}${meta ? ` • ${meta}` : ""} • ID: ${u.id}`,
      };
    });
  }, [mapWorkItemAssignees]);
  useEffect(() => {
    const firstKind = mapWorkItemKindOptions[0]?.code;
    const firstStatus = mapWorkItemStatusOptions[0]?.code;
    if (!firstKind || !firstStatus) return;

    setMapWorkItemDraft((prev) => {
      const kindExists = mapWorkItemKindOptions.some((k) => k.code === prev.kind);
      const statusExists = mapWorkItemStatusOptions.some((s) => s.code === prev.status);
      if (kindExists && statusExists) return prev;
      return {
        ...prev,
        kind: kindExists ? prev.kind : firstKind,
        status: statusExists ? prev.status : firstStatus,
      };
    });
  }, [mapWorkItemKindOptions, mapWorkItemStatusOptions]);
  const { data: geometrySemanticTypes = [] } = useQuery({
    queryKey: ["kls", "geometry-semantic-types", "home"],
    queryFn: () =>
      safeApiCall(
        () => klsSectionsApi.list({ qual_id: QUAL_ID_GEOMETRY_SEMANTIC_TYPES, limit: 1000 }),
        [],
        "Не удалось загрузить семантические типы геометрий (KLS qual=345)",
        { silent: true },
      ),
    enabled: isAuthenticated,
  });
  const { data: geometryPrimitiveTypes = [] } = useQuery({
    queryKey: ["kls", "geometry-primitive-types", "home"],
    queryFn: () =>
      safeApiCall(
        () => klsSectionsApi.list({ qual_id: QUAL_ID_GEOMETRY_PRIMITIVE_TYPES, limit: 1000 }),
        [],
        "Не удалось загрузить типы примитивов геометрий (KLS qual=343)",
        { silent: true },
      ),
    enabled: isAuthenticated,
  });
  /** Только сессия редактирования вершин Terra Draw; иначе ложное isEditing в объекте блокировало нативное создание геометрии. */
  const isMapVertexEditSession = Boolean(
    isDrawing &&
      currentDrawing != null &&
      currentDrawing.isEditing === true &&
      currentDrawing.geometryId != null,
  );

  /** Чертёж после завершения штриха (до «Сохранить») — яркий слой на карте. */
  const pendingSketchFeature = useMemo((): GeoJSON.Feature | null => {
    if (!isDrawing || isMapVertexEditSession) return null;
    const d = currentDrawing as Record<string, unknown> | null | undefined;
    const raw = d?.geoJSON ?? d?.geojson;
    if (raw == null) return null;
    try {
      const data = typeof raw === "string" ? JSON.parse(raw) : raw;
      const feature: GeoJSON.Feature =
        data && typeof data === "object" && data.type === "Feature"
          ? (data as GeoJSON.Feature)
          : { type: "Feature", geometry: data as GeoJSON.Geometry, properties: {} };
      const t = String(currentDrawing?.type ?? "").toLowerCase();
      if (t === "circle") {
        return convertCircleToPolygon(feature) as GeoJSON.Feature;
      }
      return feature.geometry ? feature : null;
    } catch {
      return null;
    }
  }, [isDrawing, isMapVertexEditSession, currentDrawing, currentDrawing?.type]);

  const { data: geometryCardDraft } = useQuery({
    queryKey: ["object-card-draft", "geometry-preview", selectedObject?.kls_id, selectedObject?.organization_id],
    queryFn: () => objectCardApi.getCardDraft(Number(selectedObject?.kls_id), Number(selectedObject?.organization_id)),
    enabled: isAuthenticated && isGeometryTypeDialogOpen && !!selectedObject?.kls_id && !!selectedObject?.organization_id,
    staleTime: 30_000,
  });

  const { data: primitiveOptionsForSemantic } = useQuery({
    queryKey: ["kls", "geometry-primitives-for-semantic", selectedGeometrySemanticKlsId],
    queryFn: () =>
      klsSectionsApi.geometryPrimitivesForSemantic(Number(selectedGeometrySemanticKlsId)),
    enabled:
      isAuthenticated &&
      isGeometryTypeDialogOpen &&
      !!selectedGeometrySemanticKlsId &&
      !Number.isNaN(Number(selectedGeometrySemanticKlsId)),
    staleTime: 60_000,
  });

  const {
    data: selectedObjectGeometries = [],
    isLoading: geometriesLoading,
    isFetching: geometriesFetching,
  } = useQuery({
    queryKey: selectedObject?.id != null ? objectQueryKeys.geometries(selectedObject.id) : ["objects", "geometries", "idle"],
    queryFn: () => {
      if (!selectedObject) return Promise.resolve([]);
      return safeApiCall(
        () => objectsApi.getGeometries(selectedObject.id),
        [],
        `Не удалось загрузить геометрию объекта #${selectedObject.id}`,
      );
    },
    enabled: !!selectedObject,
  });

  const { data: files = [] } = useQuery({
    queryKey: selectedObject?.id != null ? objectQueryKeys.files(selectedObject.id) : ["objects", "files", "idle"],
    queryFn: () => {
      if (!selectedObject) return Promise.resolve([]);
      return safeApiCall(
        () => objectsApi.getFiles(selectedObject.id),
        [],
        `Не удалось загрузить файлы объекта #${selectedObject.id}`,
      );
    },
    enabled: !!selectedObject,
  });

  // --- Handlers ---
  const selectObjectCore = useCallback(
    (
      object: GeoObject,
      options?: { focusGeometryId?: number | null; openSidebarDetails?: boolean },
    ) => {
      const openDetails = options?.openSidebarDetails !== false;
      if (!openDetails) {
        listSelectionWithoutDetailsObjectIdRef.current = object.id;
      } else {
        listSelectionWithoutDetailsObjectIdRef.current = null;
      }
      setSelectedObject(object);
      setSidebarTab(openDetails ? "details" : "objects");
      const gid =
        options && "focusGeometryId" in options ? options.focusGeometryId ?? null : null;
      setFocusedGeometryId(gid);

      const applyObjectToUrl = () => {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("objectId", String(object.id));
            if (gid != null && !Number.isNaN(Number(gid))) {
              next.set("geometryId", String(gid));
            } else {
              next.delete("geometryId");
            }
            return next;
          },
          { replace: true },
        );
      };

      if (objectIdToUrlTimeoutRef.current != null) {
        window.clearTimeout(objectIdToUrlTimeoutRef.current);
        objectIdToUrlTimeoutRef.current = null;
      }

      /*
       * Синхронный setSearchParams в одном обработчике с setSelectedObject даёт эффекту по URL снимок,
       * где searchParams уже новые, а selectedObject в замыкании ещё старый → срабатывает ветка «Детали».
       * Для просмотра из списка откладываем запись query на следующий макротаск — после commit React.
       */
      if (!openDetails) {
        const deferredObjectId = object.id;
        objectIdToUrlTimeoutRef.current = window.setTimeout(() => {
          objectIdToUrlTimeoutRef.current = null;
          const pending = listSelectionWithoutDetailsObjectIdRef.current;
          if (pending == null || Number(pending) !== Number(deferredObjectId)) {
            return;
          }
          const cur = selectedObjectRef.current;
          if (cur == null || Number(cur.id) !== Number(deferredObjectId)) {
            return;
          }
          applyObjectToUrl();
        }, 0);
      } else {
        applyObjectToUrl();
      }
    },
    [setSearchParams],
  );

  const handleSelectObject = useCallback(
    (
      object: GeoObject,
      options?: { focusGeometryId?: number | null; openSidebarDetails?: boolean },
    ) => {
      const prev = selectedObjectRef.current;
      const openDetails = options?.openSidebarDetails !== false;
      const switchingObject = prev != null && Number(prev.id) !== Number(object.id);
      const leavingDetails =
        cardDirtyRef.current &&
        prev != null &&
        (switchingObject || (!openDetails && sidebarTabRef.current === "details"));
      if (leavingDetails) {
        setCardLeaveDialog({ run: () => selectObjectCore(object, options) });
        return;
      }
      selectObjectCore(object, options);
    },
    [selectObjectCore],
  );

  const setMultiSelectedObjectIds = useCallback((ids: number[]) => {
    setMultiSelectedObjectIdsState(normalizeObjectIdList(ids));
  }, []);

  const toggleMultiSelectedObject = useCallback((objectId: number) => {
    setMultiSelectMode(true);
    setMultiSelectedObjectIdsState((prev) => toggleIdInList(prev, objectId));
  }, []);

  const addMultiSelectedObjectIds = useCallback((ids: number[]) => {
    setMultiSelectMode(true);
    setMultiSelectedObjectIdsState((prev) => mergeMultiIds(prev, ids));
  }, []);

  const clearMultiSelection = useCallback(() => {
    setMultiSelectedObjectIdsState([]);
  }, []);

  const closeObjectPanelCore = useCallback(() => {
    if (objectIdToUrlTimeoutRef.current != null) {
      window.clearTimeout(objectIdToUrlTimeoutRef.current);
      objectIdToUrlTimeoutRef.current = null;
    }
    listSelectionWithoutDetailsObjectIdRef.current = null;
    const prevId = selectedObjectRef.current?.id;
    setSelectedObject(null);
    setCardDirty(false);
    if (prevId != null) {
      setCurrentObjectId((cid) => (cid === prevId ? null : cid));
    }
    setSelectedGeometry(null);
    setSidebarTab("objects");
    setFocusedGeometryId(null);
    setMultiSelectMode(false);
    setMultiSelectedObjectIdsState([]);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("objectId");
        next.delete("geometryId");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const handleCloseObjectPanel = useCallback(() => {
    if (cardDirtyRef.current) {
      setCardLeaveDialog({ run: () => closeObjectPanelCore() });
      return;
    }
    closeObjectPanelCore();
  }, [closeObjectPanelCore]);

  const requestSidebarTabChange = useCallback((tab: SidebarTab) => {
    if (cardDirtyRef.current && sidebarTabRef.current === "details" && tab !== "details") {
      setCardLeaveDialog({ run: () => setSidebarTab(tab) });
      return;
    }
    setSidebarTab(tab);
  }, []);

  const clearGeometryFocus = useCallback(() => {
    setFocusedGeometryId(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("geometryId");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  /** Восстановление выбора из query (?objectId=&geometryId=) — deep link и обновление адреса извне. */
  useEffect(() => {
    if (!isAuthenticated || objectsLoading) return;
    const oidStr = searchParams.get("objectId");
    if (!oidStr) {
      /*
       * Нет objectId в URL, а в state объект ещё есть — рассинхрон после clear / гонка с отложенным URL.
       * Не мешаем: ждём запись objectId из списка (таймер или ref ожидания).
       */
      if (objectIdToUrlTimeoutRef.current != null) {
        return;
      }
      if (listSelectionWithoutDetailsObjectIdRef.current != null) {
        return;
      }
      if (selectedObjectRef.current != null) {
        setSelectedObject(null);
        setFocusedGeometryId(null);
      }
      return;
    }
    const objectIdNum = Number.parseInt(oidStr, 10);
    if (Number.isNaN(objectIdNum)) return;

    const pendingListObjectId = listSelectionWithoutDetailsObjectIdRef.current;
    /*
     * Пока URL не догнал объект, выбранный из списка без «Деталей», не применяем старый objectId из query.
     */
    if (
      pendingListObjectId != null &&
      selectedObject != null &&
      Number(selectedObject.id) === pendingListObjectId &&
      objectIdNum !== pendingListObjectId
    ) {
      return;
    }

    const obj = objects.find((o) => Number(o.id) === objectIdNum);
    if (!obj) return;

    const gidStr = searchParams.get("geometryId");
    let gid: number | null = null;
    if (gidStr) {
      const g = Number.parseInt(gidStr, 10);
      gid = Number.isNaN(g) ? null : g;
    }

    const sameObject =
      selectedObject != null && Number(selectedObject.id) === Number(objectIdNum);
    const sameGeom =
      focusedGeometryId === gid ||
      (focusedGeometryId == null && gid == null);
    if (sameObject && sameGeom) {
      if (listSelectionWithoutDetailsObjectIdRef.current === objectIdNum) {
        listSelectionWithoutDetailsObjectIdRef.current = null;
      }
      return;
    }

    if (listSelectionWithoutDetailsObjectIdRef.current === objectIdNum) {
      listSelectionWithoutDetailsObjectIdRef.current = null;
      setSelectedObject(obj);
      setFocusedGeometryId(gid);
      setSidebarTab("objects");
      return;
    }

    /*
     * Объект в state уже совпадает с URL, но geometryId в query и focusedGeometryId разъехались
     * (например после подгрузки геометрий / фокуса с карты). Раньше срабатывала ветка «как deep link»
     * и открывалась вкладка «Детали» — для выбора из списка это неверно.
     */
    if (sameObject && !sameGeom) {
      setFocusedGeometryId(gid);
      if (listSelectionWithoutDetailsObjectIdRef.current === objectIdNum) {
        listSelectionWithoutDetailsObjectIdRef.current = null;
      }
      return;
    }

    /*
     * Вкладку «Детали» не трогаем: только handleSelectObject(..., openSidebarDetails: true)
     * и явные действия пользователя. Иначе refetch objects / stats / таймеры URL снова
     * вызывают setSidebarTab("details") без клика по «Детали».
     */
    setSelectedObject(obj);
    setFocusedGeometryId(gid);
  }, [
    isAuthenticated,
    objectsLoading,
    objects,
    searchParams,
    selectedObject?.id,
    focusedGeometryId,
  ]);

  const drawTools = useMemo(
    () =>
      new Set([
        "draw-point",
        "draw-line",
        "draw-polygon",
        "draw-rectangle",
        "draw-circle",
        "draw-freehand",
        "draw-angled-rectangle",
        "draw-sensor",
        "draw-sector",
      ]),
    [],
  );

  /**
   * §3.1 Создание новой геометрии — только через диалог типа (KLS):
   * панель инструментов (handleToolSelect), requestStartDrawing / activateDrawingTool → openGeometryTypeDialog;
   * после подтверждения — confirmGeometryTypeSelection (единственное место, где выставляются KLS и включается рисование).
   * Редактирование контура: handleEditGeometry — без повторного диалога (тип уже задан у геометрии).
   */
  const openGeometryTypeDialog = useCallback((requestedTool: string, objectId: number | null) => {
    setPendingDrawTool(requestedTool);
    setPendingDrawObjectId(objectId);
    setSelectedGeometrySemanticKlsId(undefined);
    setSelectedGeometryPrimitiveKlsId(undefined);
    selectedGeometrySemanticRef.current = undefined;
    selectedGeometryPrimitiveRef.current = undefined;
    setIsGeometryTypeDialogOpen(true);
  }, []);

  const clearPendingCoordsImport = useCallback(() => {
    pendingImportFeatureRef.current = null;
    setPendingImportFeatureCount(null);
  }, []);

  const handleCoordsImportSuccess = useCallback(
    (result: GeometryCoordsImportSuccess) => {
      if (isMapVertexEditSession) {
        toast({
          title: "Завершите редактирование контура",
          description: "Сохраните изменения или нажмите «Отмена» в панели на карте.",
          variant: "destructive",
        });
        return;
      }
      pendingImportFeatureRef.current = result;
      setPendingImportFeatureCount(result.featureCount);
      const tool = inferDrawToolFromFeature(result.feature);
      openGeometryTypeDialog(tool, selectedObject?.id ?? null);
      if (result.featureCount > 1) {
        toast({
          title: "Импортирована первая подходящая геометрия",
          description: `В файле ${result.featureCount} объектов — на карту попала первая подходящая (индекс ${result.selectedFeatureIndex + 1}).`,
        });
      }
    },
    [isMapVertexEditSession, openGeometryTypeDialog, selectedObject?.id, toast],
  );

  const onSemanticKlsChange = useCallback((v: string | undefined) => {
    selectedGeometrySemanticRef.current = v;
    setSelectedGeometrySemanticKlsId(v);
    selectedGeometryPrimitiveRef.current = undefined;
    setSelectedGeometryPrimitiveKlsId(undefined);
  }, []);

  const onPrimitiveKlsChange = useCallback((v: string | undefined) => {
    selectedGeometryPrimitiveRef.current = v;
    setSelectedGeometryPrimitiveKlsId(v);
  }, []);

  useEffect(() => {
    const onActivateDrawing = (e: Event) => {
      const d = (e as CustomEvent<{ tool?: string; objectId?: number }>).detail;
      const tool = d?.tool ?? "draw-polygon";
      const oid = d?.objectId ?? selectedObject?.id ?? null;
      openGeometryTypeDialog(tool, oid);
    };
    window.addEventListener("activateDrawingTool", onActivateDrawing);
    return () => window.removeEventListener("activateDrawingTool", onActivateDrawing);
  }, [openGeometryTypeDialog, selectedObject?.id]);

  useEffect(() => {
    const map = mapInstance;
    if (!map) return;

    const empty: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

    const ensureLayer = () => {
      if (!map.isStyleLoaded()) return;
      if (!map.getSource(OI_MAP_GRID_SOURCE)) {
        map.addSource(OI_MAP_GRID_SOURCE, { type: "geojson", data: empty });
      }
      if (!map.getLayer(OI_MAP_GRID_LAYER)) {
        map.addLayer({
          id: OI_MAP_GRID_LAYER,
          type: "line",
          source: OI_MAP_GRID_SOURCE,
          layout: { "line-cap": "butt", "line-join": "miter" },
            paint: {
              "line-color": "#64748b",
              "line-width": 1,
              "line-opacity": 0.35,
            },
        });
      }
    };

    const refresh = () => {
      if (!map.isStyleLoaded()) return;
      ensureLayer();
      const source = map.getSource(OI_MAP_GRID_SOURCE) as
        | { setData: (data: GeoJSON.FeatureCollection) => void }
        | undefined;
      if (!source) return;
      if (!oiMapGridPrefs.grid) {
        source.setData(empty);
        return;
      }
      source.setData(
        buildOiMapGridFeatureCollection(map.getBounds(), oiMapGridPrefs.stepMeters),
      );
    };

    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(refresh, 80);
    };

    const onReady = () => {
      refresh();
    };
    if (map.isStyleLoaded()) onReady();
    map.on("load", onReady);
    map.on("style.load", onReady);
    map.on("moveend", schedule);
    map.on("zoomend", schedule);
    map.on("styledata", schedule);

    return () => {
      if (timer) clearTimeout(timer);
      map.off("moveend", schedule);
      map.off("zoomend", schedule);
      map.off("styledata", schedule);
      map.off("load", onReady);
      map.off("style.load", onReady);
      try {
        if (map.getLayer(OI_MAP_GRID_LAYER)) map.removeLayer(OI_MAP_GRID_LAYER);
        if (map.getSource(OI_MAP_GRID_SOURCE)) map.removeSource(OI_MAP_GRID_SOURCE);
      } catch {
        /* style may be gone */
      }
    };
  }, [mapInstance, oiMapGridPrefs.grid, oiMapGridPrefs.stepMeters]);

  const [oiNomPrefsTick, setOiNomPrefsTick] = useState(0);
  useEffect(() => {
    const sync = () => setOiNomPrefsTick((n) => n + 1);
    window.addEventListener(OI_NOM_PREFS_EVENT, sync);
    return () => window.removeEventListener(OI_NOM_PREFS_EVENT, sync);
  }, []);

  useEffect(() => {
    const map = mapInstance;
    if (!map) return;

    const empty: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };
    let aborted = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const ensureLayers = () => {
      if (!map.isStyleLoaded()) return;
      if (!map.getSource(OI_NOM_SOURCE)) {
        map.addSource(OI_NOM_SOURCE, { type: "geojson", data: empty });
      }
      if (!map.getLayer(OI_NOM_FILL_LAYER)) {
        map.addLayer({
          id: OI_NOM_FILL_LAYER,
          type: "fill",
          source: OI_NOM_SOURCE,
          paint: { "fill-color": "#facc15", "fill-opacity": 0.04 },
        });
      }
      if (!map.getLayer(OI_NOM_LINE_LAYER)) {
        map.addLayer({
          id: OI_NOM_LINE_LAYER,
          type: "line",
          source: OI_NOM_SOURCE,
          paint: {
            "line-color": "#eab308",
            "line-width": 1.25,
            "line-opacity": 0.85,
          },
        });
      }
      if (!map.getLayer(OI_NOM_LABEL_LAYER)) {
        map.addLayer({
          id: OI_NOM_LABEL_LAYER,
          type: "symbol",
          source: OI_NOM_SOURCE,
          layout: {
            "text-field": ["get", "nomenclature"],
            "text-size": 14,
            "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
            "text-allow-overlap": false,
            "symbol-placement": "point",
          },
          paint: {
            "text-color": "#dc2626",
            "text-halo-color": "#ffffff",
            "text-halo-width": 1.5,
          },
        });
      }
      if (!map.getSource(OI_NOM_HIT_SOURCE)) {
        map.addSource(OI_NOM_HIT_SOURCE, { type: "geojson", data: empty });
      }
      if (!map.getLayer(OI_NOM_HIT_FILL)) {
        map.addLayer({
          id: OI_NOM_HIT_FILL,
          type: "fill",
          source: OI_NOM_HIT_SOURCE,
          paint: { "fill-color": "#2563eb", "fill-opacity": 0.18 },
        });
      }
      if (!map.getLayer(OI_NOM_HIT_LINE)) {
        map.addLayer({
          id: OI_NOM_HIT_LINE,
          type: "line",
          source: OI_NOM_HIT_SOURCE,
          paint: {
            "line-color": "#1d4ed8",
            "line-width": 2.5,
            "line-opacity": 0.95,
          },
        });
      }
    };

    const refresh = async () => {
      if (!map.isStyleLoaded() || aborted) return;
      ensureLayers();
      const source = map.getSource(OI_NOM_SOURCE) as
        | { setData: (data: GeoJSON.FeatureCollection) => void }
        | undefined;
      if (!source) return;
      const scales = enabledOiNomScaleList(loadOiNomPrefs());
      if (!scales.length) {
        source.setData(empty);
        return;
      }
      const b = map.getBounds();
      const west = b.getWest();
      const south = b.getSouth();
      const east = b.getEast();
      const north = b.getNorth();
      try {
        const parts = await Promise.all(
          scales.map((scale) =>
            nomenclatureSheetsApi.geojson({
              scale,
              west,
              south,
              east,
              north,
              limit: 600,
            }),
          ),
        );
        if (aborted) return;
        const features = parts.flatMap((fc) => fc.features ?? []);
        source.setData({
          type: "FeatureCollection",
          features: features as GeoJSON.Feature[],
        });
      } catch {
        if (!aborted) source.setData(empty);
      }
    };

    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void refresh();
      }, 120);
    };

    const onReady = () => {
      void refresh();
    };
    if (map.isStyleLoaded()) onReady();
    map.on("load", onReady);
    map.on("style.load", onReady);
    map.on("moveend", schedule);
    map.on("zoomend", schedule);

    return () => {
      aborted = true;
      if (timer) clearTimeout(timer);
      map.off("moveend", schedule);
      map.off("zoomend", schedule);
      map.off("load", onReady);
      map.off("style.load", onReady);
      try {
        for (const id of [
          OI_NOM_LABEL_LAYER,
          OI_NOM_LINE_LAYER,
          OI_NOM_FILL_LAYER,
          OI_NOM_HIT_LINE,
          OI_NOM_HIT_FILL,
        ]) {
          if (map.getLayer(id)) map.removeLayer(id);
        }
        if (map.getSource(OI_NOM_SOURCE)) map.removeSource(OI_NOM_SOURCE);
        if (map.getSource(OI_NOM_HIT_SOURCE)) map.removeSource(OI_NOM_HIT_SOURCE);
      } catch {
        /* style may be gone */
      }
    };
  }, [mapInstance, oiNomPrefsTick]);

  const handleNomHighlight = useCallback(
    (sheets: NomenclatureSheetHit[]) => {
      const map = mapInstance;
      if (!map?.isStyleLoaded()) return;
      const source = map.getSource(OI_NOM_HIT_SOURCE) as
        | { setData: (data: GeoJSON.FeatureCollection) => void }
        | undefined;
      if (!source) return;
      source.setData({
        type: "FeatureCollection",
        features: sheets.map((sh) => ({
          type: "Feature",
          geometry: sh.geom,
          properties: {
            nomenclature: sh.nomenclature,
            scale: sh.scale,
          },
        })),
      });
    },
    [mapInstance],
  );

  const handleNomClearHighlight = useCallback(() => {
    const map = mapInstance;
    if (!map?.isStyleLoaded()) return;
    const source = map.getSource(OI_NOM_HIT_SOURCE) as
      | { setData: (data: GeoJSON.FeatureCollection) => void }
      | undefined;
    source?.setData({ type: "FeatureCollection", features: [] });
  }, [mapInstance]);

  const handleNomLocateObject = useCallback(
    async (objectId: number) => {
      try {
        const obj = await objectsApi.get(objectId);
        handleSelectObject(obj, { openSidebarDetails: true });
      } catch {
        toast({
          title: "Не удалось открыть объект",
          variant: "destructive",
        });
      }
    },
    [handleSelectObject, toast],
  );

  const handleToolSelect = useCallback(
    (tool: string) => {
      if (isMapVertexEditSession) {
        toast({
          title: "Завершите редактирование контура",
          description: "Сохраните изменения или нажмите «Отмена» в панели на карте.",
          variant: "destructive",
        });
        return;
      }
      if (drawTools.has(tool)) {
        openGeometryTypeDialog(tool, selectedObject?.id ?? null);
        return;
      }
      if (tool !== "measure") {
        setMeasureLabel(null);
        setMeasureHudCursor(null);
        setMeasurePoint(null);
      }
      setActiveTool(tool);
      setIsDrawing(false);
    },
    [drawTools, isMapVertexEditSession, openGeometryTypeDialog, selectedObject?.id, toast],
  );

  const handleRoutingClose = useCallback(() => {
    setActiveTool("select");
  }, []);

  const handleMeasureModeChange = useCallback((mode: MeasureMode) => {
    setMeasureMode(mode);
    setMeasureLabel(null);
    setMeasureHudCursor(null);
    setMeasurePoint(null);
  }, []);

  const handleMeasureDistanceChange = useCallback(
    (
      _meters: number | null,
      label: string | null,
      cursor?: MeasureHudCursor | null,
    ) => {
      setMeasureLabel(label);
      setMeasureHudCursor(cursor ?? null);
    },
    [],
  );

  const handleMeasurePoint = useCallback((point: MeasurePointResult | null) => {
    setMeasurePoint(point);
  }, []);

  const handleMeasureExit = useCallback(() => {
    setMeasureLabel(null);
    setMeasureHudCursor(null);
    setMeasurePoint(null);
    setActiveTool("select");
  }, []);

  const handleContourSelectModeChange = useCallback((mode: ContourSelectMode) => {
    setContourSelectMode(mode);
  }, []);

  const clearContourFilter = useCallback(() => {
    setContourFilter(null);
  }, []);

  const handleContourSelectComplete = useCallback(
    async (feature: Feature<Polygon>) => {
      setActiveTool("select");
      try {
        const res = await spatialApi.intersect(
          feature as unknown as Record<string, unknown>,
          CONTOUR_INTERSECT_LIMIT,
        );
        const ids = Array.from(
          new Set(
            (res.features ?? [])
              .map((f) => f.object_id)
              .filter((id): id is number => Number.isFinite(id)),
          ),
        );
        setContourFilter({ objectIds: ids, feature });
        if ((res.features?.length ?? 0) >= CONTOUR_INTERSECT_LIMIT) {
          toast({
            title: "Показаны первые результаты",
            description: `Лимит ${CONTOUR_INTERSECT_LIMIT} пересечений. Уточните область контура.`,
          });
        } else {
          toast({
            title: "Фильтр по контуру",
            description:
              ids.length === 0
                ? "В контуре нет объектов"
                : `Объектов в контуре: ${ids.length}`,
          });
        }
      } catch (err) {
        toast({
          title: "Не удалось отфильтровать по контуру",
          description: err instanceof Error ? err.message : "Ошибка spatial/intersect",
          variant: "destructive",
        });
      }
    },
    [toast],
  );

  const handleContourSelectExit = useCallback(() => {
    setActiveTool("select");
  }, []);

  const copyMeasureCoords = useCallback(async () => {
    if (!measurePoint) return;
    try {
      await navigator.clipboard.writeText(measurePoint.text);
      toast({ title: "Скопировано", description: measurePoint.text });
    } catch {
      toast({
        title: "Не удалось скопировать",
        description: measurePoint.text,
        variant: "destructive",
      });
    }
  }, [measurePoint, toast]);

  const resetMapWorkItemDraft = useCallback(() => {
    const defaultKind = mapWorkItemKindOptions[0]?.code ?? "incident";
    const defaultStatus = mapWorkItemStatusOptions[0]?.code ?? "new";
    setMapWorkItemDraft({
      kind: defaultKind,
      title: "",
      description: "",
      status: defaultStatus,
      priority: 3,
      assignedToUserId: "",
    });
  }, [mapWorkItemKindOptions, mapWorkItemStatusOptions]);

  const handleMapWorkItemPickPoint = useCallback(
    async (lng: number, lat: number) => {
      if (!hasPermission("work_items.create")) {
        toast({
          title: "Недостаточно прав",
          description: "Требуется разрешение work_items.create.",
          variant: "destructive",
        });
        return;
      }
      const point: GeoJSON.Feature<GeoJSON.Point> = {
        type: "Feature",
        properties: {},
        geometry: { type: "Point", coordinates: [lng, lat] },
      };
      try {
        const analyzed = await geometriesApi.analyze({
          geojson: point,
          // 0m для Point даёт нестабильный search zone в SQL (ST_Buffer(..., 0)),
          // из-за чего пересечения иногда не находятся даже внутри полигона.
          tolerance_meters: 1,
          limit: 20,
          ratio_threshold: 1,
        });
        const linked = analyzed.candidates.find(
          (c) => (c.relation === "inside" || c.relation === "intersects") && c.can_attach_geometry,
        );
        setMapWorkItemAutoLink(
          linked
            ? { objectId: linked.object_id, geometryId: linked.geometry_id, objectName: linked.object_name }
            : null,
        );
      } catch (e) {
        // Не блокируем создание: если анализ недоступен, создаём точку без привязки.
        setMapWorkItemAutoLink(null);
        toast({
          title: "Автопривязка недоступна",
          description: e instanceof Error ? e.message : "Точка будет сохранена без привязки.",
          variant: "destructive",
        });
      }
      setMapWorkItemAnchor({ lng, lat });
      resetMapWorkItemDraft();
      setIsMapWorkItemDialogOpen(true);
    },
    [geometriesApi, hasPermission, resetMapWorkItemDraft, toast],
  );

  const submitMapWorkItem = useCallback(async () => {
    const anchor = mapWorkItemAnchor;
    const title = mapWorkItemDraft.title.trim();
    if (!anchor) return;
    if (!title) {
      toast({
        title: "Укажите заголовок",
        description: "Поле «Заголовок» обязательно.",
        variant: "destructive",
      });
      return;
    }
    setMapWorkItemSubmitting(true);
    try {
      const fallbackStatus = mapWorkItemStatusOptions[0]?.code ?? "new";
      const created = await workItemsApi.create({
        kind: mapWorkItemDraft.kind,
        title,
        description: mapWorkItemDraft.description.trim() || undefined,
        status: mapWorkItemDraft.status || fallbackStatus,
        priority: mapWorkItemDraft.priority,
        objectId: mapWorkItemAutoLink?.objectId,
        geometryId: mapWorkItemAutoLink?.geometryId,
        assignedToUserId: mapWorkItemDraft.assignedToUserId.trim() || undefined,
        sourceSystem: "map",
        sourcePayload: {
          location_lon: anchor.lng,
          location_lat: anchor.lat,
          anchor_source: "map_click",
        },
      });
      setIsMapWorkItemDialogOpen(false);
      setMapWorkItemAnchor(null);
      setMapWorkItemAutoLink(null);
      resetMapWorkItemDraft();
      setWorkItemsPinnedPoints((prev) => {
        const point: WorkItemMapPoint = {
          id: created.id,
          coordinates: [anchor.lng, anchor.lat],
          kind: mapWorkItemDraft.kind,
          title,
          description: mapWorkItemDraft.description.trim() || null,
          status: mapWorkItemDraft.status || fallbackStatus,
          priority: mapWorkItemDraft.priority,
          objectId: mapWorkItemAutoLink?.objectId ?? null,
          geometryId: mapWorkItemAutoLink?.geometryId ?? null,
          sourceSystem: "map",
          createdAt: created.createdAt,
          anchorSource: "payload_location",
        };
        const next = [point, ...prev.filter((p) => p.id !== point.id)];
        return next.slice(0, 300);
      });
      setWorkItemsRefreshEpoch((v) => v + 1);
      void queryClient.invalidateQueries({ queryKey: ["work-items"] });
      toast({
        title: `Запись #${created.id} создана`,
        description: mapWorkItemAutoLink?.objectId
          ? `Привязан к объекту «${mapWorkItemAutoLink.objectName ?? mapWorkItemAutoLink.objectId}».`
          : "Создан без привязки к объекту.",
      });
    } catch (e) {
      toast({
        title: "Ошибка создания записи",
        description: e instanceof Error ? e.message : "Не удалось сохранить запись",
        variant: "destructive",
      });
    } finally {
      setMapWorkItemSubmitting(false);
    }
  }, [
    mapWorkItemAnchor,
    mapWorkItemAutoLink,
    mapWorkItemDraft,
    mapWorkItemStatusOptions,
    queryClient,
    resetMapWorkItemDraft,
    toast,
  ]);

  const toolbarVisualActiveTool = isMapVertexEditSession ? "select" : activeTool;

  const handleGeometrySelect = (geometry: Geometry | null) => {
    setSelectedGeometry(geometry);
    toast({
      title: "Геометрия выбрана",
      description: "Выбранная геометрия готова для анализа ассистентом",
      variant: "default",
    });
  };

  const handleEditObject = useCallback((object: GeoObject) => {
    setObjectToEdit(object);
    setIsEditObjectFormOpen(true);
  }, []);

  const focusGeometryOnMap = useCallback((geometry: Geometry) => {
    window.dispatchEvent(
      new CustomEvent("focusGeometryOnMap", { detail: { geometry, source: "object" } })
    );
  }, []);

  const handleEditGeometry = useCallback((geometry: any, objectId: number) => {
    if (!geometry || !geometry.id) return;
    const mappedTool = mapPrimitiveCodeToDrawTool(
      geometry.geometry_primitive_code,
      typeof geometry.type === "string" ? geometry.type : undefined
    );

    const initialEditingState = {
      geoJSON: geometry.geojson,
      originalGeoJSON: geometry.geojson,
      type: geometry.type,
      isEditing: true,
      geometryId: geometry.id,
      objectId,
      name: geometry.name,
      originalName: geometry.name,
      description: geometry.description,
      originalDescription: geometry.description,
      geometry_kls_id: geometry.geometry_kls_id,
      geometry_primitive_kls_id: geometry.geometry_primitive_kls_id,
      geometry_primitive_code: geometry.geometry_primitive_code,
      style: geometry.style,
      originalStyle: geometry.style,
    };
    setOriginalEditingDrawing(initialEditingState);
    setCurrentDrawing(initialEditingState);
    setActiveTool(mappedTool);
    setIsDrawing(true);
    setCurrentObjectId(objectId);
    window.dispatchEvent(
      new CustomEvent("editGeometry", { detail: { geometry, objectId } })
    );
  }, []);

  const handleStartDrawing = useCallback((tool: string, objectId: number) => {
    openGeometryTypeDialog(tool, objectId);
  }, [openGeometryTypeDialog]);

  const handleDrawingComplete = useCallback((drawingResult: any) => {
    setCurrentDrawing((prev: any) => {
      const base = {
        ...(prev || {}),
        ...(drawingResult || {}),
        geometry_kls_id: prev?.geometry_kls_id ?? drawingResult?.geometry_kls_id,
        geometry_primitive_kls_id:
          prev?.geometry_primitive_kls_id ?? drawingResult?.geometry_primitive_kls_id,
        geometry_primitive_code:
          prev?.geometry_primitive_code ?? drawingResult?.geometry_primitive_code,
        originalGeoJSON: prev?.originalGeoJSON,
        originalName: prev?.originalName,
        originalDescription: prev?.originalDescription,
        originalStyle: prev?.originalStyle,
        objectId: prev?.objectId ?? currentObjectId ?? undefined,
      };
      /* Нативное создание не шлёт isEditing; не тянем geometryId/isEditing с прошлой сессии — иначе pendingSketch гасится как «режим вершин». */
      if (drawingResult?.isEditing !== true) {
        base.isEditing = false;
        base.geometryId = undefined;
      }
      return base;
    });
  }, [currentObjectId]);

  const handleDrawingSave = useCallback(() => {
    setMapFirstGeometryFlow(false);
    setIsDrawing(false);
    setActiveTool("select");
    setCurrentObjectId(null);
    setOriginalEditingDrawing(null);
    setMvtTilesEpoch((n) => n + 1);
    const oid = selectedObject?.id;
    if (oid != null) {
      void queryClient.invalidateQueries({ queryKey: objectQueryKeys.geometries(oid) });
      void queryClient.invalidateQueries({ queryKey: objectQueryKeys.detail(oid) });
    }
    void queryClient.invalidateQueries({ queryKey: ["geometries"] });
    void queryClient.invalidateQueries({ queryKey: ["stats"] });
  }, [selectedObject?.id, queryClient]);

  const handleMapFirstCommit = useCallback(async () => {
    setAnalyzeLoading(true);
    try {
      const d = currentDrawing;
      if (!d) return;
      let geoJSONData: unknown =
        typeof d.geoJSON === "string" ? JSON.parse(d.geoJSON) : (d.geoJSON || d);
      if (d.type === "circle" || activeTool === "draw-circle") {
        geoJSONData = convertCircleToPolygon(geoJSONData as GeoJSON.Feature);
      }
      const g = geoJSONData as GeoJSON.Feature | GeoJSON.Geometry;
      const geojson: GeoJSON.Feature =
        g && typeof g === "object" && "type" in g && g.type === "Feature"
          ? (g as GeoJSON.Feature)
          : { type: "Feature", properties: {}, geometry: g as GeoJSON.Geometry };
      const res = await geometriesApi.analyze({
        geojson,
        tolerance_meters: 5,
        limit: 50,
        ratio_threshold: 0.9,
      });
      setGeometryAnalyzeResult(res);
      setIsGeometryAnalyzeDialogOpen(true);
    } catch (e) {
      console.error(e);
      toast({
        title: "Анализ геометрии",
        description: e instanceof Error ? e.message : "Не удалось выполнить запрос",
        variant: "destructive",
      });
    } finally {
      setAnalyzeLoading(false);
    }
  }, [currentDrawing, activeTool, toast]);

  const applyAnalyzedObjectFromMapFirst = useCallback(
    (objectId: number) => {
      const obj = objects.find((o) => o.id === objectId);
      if (!obj) {
        toast({ title: "Объект не найден в списке", variant: "destructive" });
        return;
      }
      handleSelectObject(obj, { openSidebarDetails: true });
      setCurrentObjectId(objectId);
      setCurrentDrawing((prev: Record<string, unknown> | null) =>
        prev ? { ...prev, objectId, object_id: objectId, mapFirst: false } : prev,
      );
      setMapFirstGeometryFlow(false);
      setIsGeometryAnalyzeDialogOpen(false);
      setGeometryAnalyzeResult(null);
      toast({
        title: "Объект выбран",
        description: "Нажмите «Сохранить» на панели рисования, чтобы добавить геометрию к объекту.",
      });
    },
    [objects, handleSelectObject, toast],
  );

  /**
   * После создания объекта из формы:
   * - если ушло initial_geometry / addGeometry — закрыть сессию рисования и обновить тайлы;
   * иначе (старый путь без контура в теле) — подставить objectId в панель для одного «Сохранить».
   */
  const handleObjectCreateSuccessForPendingGeometry = useCallback(
    (created: GeoObject) => {
      if (initialGeometryForObjectForm) {
        setInitialGeometryForObjectForm(null);
        setMapFirstGeometryFlow(false);
        setIsDrawing(false);
        setActiveTool("select");
        setCurrentObjectId(null);
        setCurrentDrawing(null);
        setOriginalEditingDrawing(null);
        void queryClient.invalidateQueries({ queryKey: objectQueryKeys.geometries(created.id) });
        void queryClient.invalidateQueries({ queryKey: ["objects"] });
        setMvtTilesEpoch((n) => n + 1);
        handleSelectObject(created, { openSidebarDetails: true });
        return;
      }

      const d = currentDrawingRef.current as Record<string, unknown> | null;
      const hasGeo = Boolean(d?.geoJSON);
      const noObject =
        (d?.objectId == null || d?.objectId === undefined) &&
        (d?.object_id == null || d?.object_id === undefined);
      const pendingMapFirst = isDrawing && hasGeo && noObject;
      if (!pendingMapFirst) return;

      setCurrentObjectId(created.id);
      setCurrentDrawing((prev: any) =>
        prev?.geoJSON ? { ...prev, objectId: created.id, object_id: created.id } : prev,
      );
      setMapFirstGeometryFlow(false);
      handleSelectObject(created, { openSidebarDetails: true });
      toast({
        title: "Следующий шаг",
        description:
          "Нажмите «Сохранить изменения» на панели рисования — контур запишется в созданный объект.",
      });
    },
    [
      initialGeometryForObjectForm,
      isDrawing,
      handleSelectObject,
      toast,
      queryClient,
    ],
  );

  const handleDrawingCancel = useCallback(() => {
    if (currentDrawing?.isEditing && originalEditingDrawing) {
      window.dispatchEvent(
        new CustomEvent("restoreEditingGeometry", {
          detail: {
            geometry: {
              id: originalEditingDrawing.geometryId,
              object_id: originalEditingDrawing.objectId,
              geojson: originalEditingDrawing.geoJSON,
              type: originalEditingDrawing.type,
              name: originalEditingDrawing.name || "",
              description: originalEditingDrawing.description || "",
              style: originalEditingDrawing.style || {},
            },
            objectId: originalEditingDrawing.objectId,
          },
        }),
      );
    }
    setMapFirstGeometryFlow(false);
    setIsGeometryAnalyzeDialogOpen(false);
    setGeometryAnalyzeResult(null);
    setIsDrawing(false);
    setActiveTool("select");
    setCurrentObjectId(null);
    setCurrentDrawing(null);
    setOriginalEditingDrawing(null);
  }, [currentDrawing?.isEditing, originalEditingDrawing]);

  /* Terra/single activate failed — контроллер abort'ит сессию; снимаем isDrawing. */
  useEffect(() => {
    const onAbort = () => {
      handleDrawingCancel();
    };
    window.addEventListener("gpml-draw-session-abort", onAbort);
    return () => window.removeEventListener("gpml-draw-session-abort", onAbort);
  }, [handleDrawingCancel]);

  const primitiveCodeByKlsId = useMemo(() => {
    const map = new globalThis.Map<number, string>();
    for (const item of geometryPrimitiveTypes) {
      if (typeof item?.kls_id !== "number") continue;
      let code = item.kls_code ? String(item.kls_code).trim().toLowerCase() : "";
      if (!code) {
        const n = `${item.kls_namef || ""} ${item.kls_names || ""}`.toLowerCase();
        const manyRu = n.includes("множество");
        if (n.includes("multiline") || n.includes("multi-line") || (manyRu && n.includes("лин"))) {
          code = "multilinestring";
        } else if (n.includes("multipoint") || (manyRu && (n.includes("точк") || n.includes("точек")))) {
          code = "multipoint";
        } else if (
          (manyRu && (n.includes("полигон") || n.includes("полигонов"))) ||
          (n.includes("multi") && (n.includes("polygon") || n.includes("полигон")))
        ) {
          code = "multipolygon";
        } else if (n.includes("line") || n.includes("линия")) code = "linestring";
        else if (n.includes("point") || n.includes("точк")) code = "point";
        else if (n.includes("polygon") || n.includes("полигон")) code = "polygon";
      }
      map.set(item.kls_id, code || "polygon");
    }
    return map;
  }, [geometryPrimitiveTypes]);

  const selectedSemanticType = useMemo(
    () => geometrySemanticTypes.find((item) => String(item.kls_id) === selectedGeometrySemanticKlsId) ?? null,
    [geometrySemanticTypes, selectedGeometrySemanticKlsId],
  );
  const geometryTemplateFieldsPreview = useMemo(() => {
    if (!selectedGeometrySemanticKlsId) return [] as CardTemplateField[];
    const semanticKlsId = Number(selectedGeometrySemanticKlsId);
    const fields = geometryCardDraft?.template?.fields ?? [];
    return fields
      .filter((field) => field.applies_to === "geometry")
      .filter((field) => field.geometry_type_kls_id == null || field.geometry_type_kls_id === semanticKlsId)
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  }, [geometryCardDraft?.template?.fields, selectedGeometrySemanticKlsId]);
  const geometryConstraintHints = useMemo(() => {
    if (geometryTemplateFieldsPreview.length === 0) return [] as string[];
    const hints: string[] = [];
    for (const field of geometryTemplateFieldsPreview) {
      const rules = (field.validation_rules ?? {}) as Record<string, unknown>;
      if (rules.min != null || rules.max != null) {
        hints.push(
          `${field.label}: диапазон ${rules.min ?? "-∞"} .. ${rules.max ?? "+∞"}`,
        );
      }
      if (rules.min_length != null || rules.max_length != null) {
        hints.push(
          `${field.label}: длина ${rules.min_length ?? 0}..${rules.max_length ?? "∞"} символов`,
        );
      }
      if (rules.pattern != null) {
        hints.push(`${field.label}: формат по шаблону`);
      }
      if (rules.allowed_values != null && Array.isArray(rules.allowed_values)) {
        hints.push(`${field.label}: ограниченный набор значений`);
      }
      if (field.is_required) {
        hints.push(`${field.label}: обязательное поле`);
      }
    }
    return hints;
  }, [geometryTemplateFieldsPreview]);

  const geometryPrimitiveOptions = useMemo(() => {
    const rows = primitiveOptionsForSemantic?.items;
    if (rows && rows.length > 0) return rows;
    return geometryPrimitiveTypes;
  }, [primitiveOptionsForSemantic?.items, geometryPrimitiveTypes]);

  const spatialHintsFromTemplate = useMemo(() => {
    if (!selectedGeometrySemanticKlsId || !geometryCardDraft?.template?.geometry_rules?.length) {
      return [] as string[];
    }
    const sid = Number(selectedGeometrySemanticKlsId);
    const rule = geometryCardDraft.template.geometry_rules.find(
      (r) => r.geometry_semantic_kls_id === sid,
    );
    return spatialConfigToUserHints(rule?.spatial_config ?? undefined);
  }, [geometryCardDraft?.template?.geometry_rules, selectedGeometrySemanticKlsId]);

  const confirmGeometryTypeSelection = useCallback(() => {
    const rawSem = selectedGeometrySemanticRef.current ?? selectedGeometrySemanticKlsId;
    const rawPrim = selectedGeometryPrimitiveRef.current ?? selectedGeometryPrimitiveKlsId;
    const sem = rawSem != null && rawSem !== "" ? String(rawSem).trim() : "";
    const prim = rawPrim != null && rawPrim !== "" ? String(rawPrim).trim() : "";
    if (!sem || !prim) {
      toast({
        title: "Выберите типы геометрии",
        description: "Нужно выбрать семантический тип и тип примитива.",
        variant: "destructive",
      });
      return;
    }

    const primitiveKlsId = Number(prim);
    const primitiveCode = primitiveCodeByKlsId.get(primitiveKlsId) ?? "polygon";
    const targetTool = resolveDrawToolForPrimitive(primitiveCode, pendingDrawTool);
    const targetObjectId = pendingDrawObjectId ?? selectedObject?.id ?? null;
    const imported = pendingImportFeatureRef.current;
    // Сбрасываем только после успешной валидации KLS (иначе пользователь потеряет импорт при ошибке выбора).
    clearPendingCoordsImport();

    if (imported) {
      const geomType = String(imported.feature.geometry?.type ?? "Polygon");
      const drawingBase = {
        isEditing: false,
        geometry_kls_id: Number(sem),
        geometry_primitive_kls_id: primitiveKlsId,
        geometry_primitive_code: primitiveCode,
        geoJSON: imported.feature,
        type: geomType.toLowerCase(),
      };

      if (!targetObjectId) {
        setCurrentDrawing(drawingBase);
        setCurrentObjectId(null);
        setMapFirstGeometryFlow(true);
      } else {
        setCurrentDrawing({ ...drawingBase, objectId: targetObjectId });
        setCurrentObjectId(targetObjectId);
        setMapFirstGeometryFlow(false);
      }
      setActiveTool(targetTool);
      setIsDrawing(true);
      setIsGeometryTypeDialogOpen(false);
      window.dispatchEvent(
        new CustomEvent("focusGeometryOnMap", {
          detail: {
            geometry: { geojson: imported.feature, type: geomType },
            source: "object",
          },
        }),
      );
      toast({
        title: targetObjectId ? "Контур импортирован" : "Контур импортирован (map-first)",
        description: targetObjectId
          ? "Проверьте превью на карте и нажмите «Сохранить» на панели рисования."
          : "Нажмите «Сохранить» — система предложит существующие объекты или создание нового.",
      });
      return;
    }

    if (!targetObjectId) {
      setCurrentDrawing({
        isEditing: false,
        geometry_kls_id: Number(sem),
        geometry_primitive_kls_id: primitiveKlsId,
        geometry_primitive_code: primitiveCode,
      });
      setCurrentObjectId(null);
      setMapFirstGeometryFlow(true);
      setActiveTool(targetTool);
      setIsDrawing(true);
      setIsGeometryTypeDialogOpen(false);
      toast({
        title: "Режим: контур без объекта",
        description:
          "Нарисуйте геометрию, затем нажмите «Сохранить» — система предложит существующие объекты или создание нового.",
      });
      return;
    }

    setCurrentDrawing({
      isEditing: false,
      objectId: targetObjectId,
      geometry_kls_id: Number(sem),
      geometry_primitive_kls_id: primitiveKlsId,
      geometry_primitive_code: primitiveCode,
    });
    setCurrentObjectId(targetObjectId);
    setMapFirstGeometryFlow(false);
    setActiveTool(targetTool);
    setIsDrawing(true);
    setIsGeometryTypeDialogOpen(false);
    toast({
      title: "Режим рисования",
      description: "Кликните по карте, чтобы поставить точки. Завершите двойным кликом (линия) или замкнув контур (полигон).",
    });
  }, [
    clearPendingCoordsImport,
    pendingDrawObjectId,
    pendingDrawTool,
    primitiveCodeByKlsId,
    resolveDrawToolForPrimitive,
    selectedGeometryPrimitiveKlsId,
    selectedGeometrySemanticKlsId,
    selectedObject?.id,
    toast,
  ]);

  confirmGeometryTypesRef.current = confirmGeometryTypeSelection;

  const refreshMapVectorTiles = useCallback(() => {
    setMvtTilesEpoch((n) => n + 1);
  }, []);

  // --- Context Value ---
  const contextValue = useMemo<MapUiContextType>(() => ({
    selectedObject,
    selectObject: handleSelectObject,
    clearSelectedObject: handleCloseObjectPanel,
    multiSelectMode,
    setMultiSelectMode,
    multiSelectedObjectIds,
    toggleMultiSelectedObject,
    setMultiSelectedObjectIds,
    addMultiSelectedObjectIds,
    clearMultiSelection,
    sidebarTab,
    setSidebarTab,
    isSidebarCollapsed,
    toggleSidebar: () => setIsSidebarCollapsed((prev) => !prev),
    activeTool,
    setActiveTool,
    isDrawing,
    currentDrawing,
    currentObjectId,
    selectedGeometry,
    setSelectedGeometry,
    focusedGeometryId,
    setFocusedGeometryId,
    clearGeometryFocus,
    activeView,
    setActiveView,
    cadastralLayersVisible,
    toggleCadastralLayers: () => setCadastralLayersVisible((prev) => !prev),
    cadastralOpacity,
    setCadastralOpacity,
    telegramIncidentsVisible,
    toggleTelegramIncidents: () => setTelegramIncidentsVisible((prev) => !prev),
    telegramOpacity,
    setTelegramOpacity,
    workItemsVisible,
    toggleWorkItemsVisible: () => {
      setWorkItemsUserTouched(true);
      setWorkItemsVisible((prev) => !prev);
    },
    workItemsOpacity,
    setWorkItemsOpacity,
    showWorkItemCreateButton,
    setShowWorkItemCreateButton,
    mapSearchQuery,
    setMapSearchQuery,
    showBaseLayerSwitcher,
    setShowBaseLayerSwitcher,
    showMapExportButton,
    setShowMapExportButton,
    mvtFilters,
    setMvtFilters,
    mvtLayerSettings,
    setMvtLayerSettings,
    referenceLayerUi,
    setReferenceLayerEnabled,
    setReferenceLayerOpacity,
    refreshMapVectorTiles,
    requestEditObject: handleEditObject,
    requestEditGeometry: handleEditGeometry,
    requestStartDrawing: handleStartDrawing,
    focusGeometryOnMap,
  }), [
    selectedObject, handleSelectObject, handleCloseObjectPanel,
    multiSelectMode, multiSelectedObjectIds,
    toggleMultiSelectedObject, setMultiSelectedObjectIds,
    addMultiSelectedObjectIds, clearMultiSelection,
    sidebarTab, isSidebarCollapsed,
    activeTool, isDrawing, currentDrawing, currentObjectId,
    selectedGeometry, activeView,
    focusedGeometryId,
    clearGeometryFocus,
    cadastralLayersVisible, cadastralOpacity,
    telegramIncidentsVisible, telegramOpacity,
    workItemsUserTouched, workItemsVisible, workItemsOpacity,
    showWorkItemCreateButton,
    mapSearchQuery,
    showBaseLayerSwitcher,
    showMapExportButton,
    mvtFilters,
    mvtLayerSettings,
    referenceLayerUi,
    setReferenceLayerEnabled,
    setReferenceLayerOpacity,
    refreshMapVectorTiles,
    handleEditObject, handleEditGeometry, handleStartDrawing,
    focusGeometryOnMap,
  ]);

  return (
    <MapUiContext.Provider value={contextValue}>
      <MainWorkspaceLayout
        header={(
          <Header
            className="sticky top-0 z-50"
            onSearch={(query) => setMapSearchQuery(query)}
          />
        )}
        sidebar={(
          <Sidebar
            objects={objects || []}
            objectTypes={objectTypes || []}
            organizations={organizations || []}
            isLoading={objectsLoading}
            onCreateObject={() => setIsObjectFormOpen(!isObjectFormOpen)}
            selectedObjectGeometries={selectedObjectGeometries as Geometry[] || []}
            selectedObjectFiles={files || []}
            geometriesLoading={geometriesLoading}
            onCardDirtyChange={setCardDirty}
            requestSidebarTabChange={requestSidebarTabChange}
            contourFilterObjectIds={contourFilter?.objectIds ?? null}
            onClearContourFilter={clearContourFilter}
          />
        )}
        toolbar={(
          <>
            <MapToolbar
              activeTool={activeTool}
              visualActiveTool={toolbarVisualActiveTool}
              lockToolsForEditSession={isMapVertexEditSession}
              onToolSelect={handleToolSelect}
              measureMode={measureMode}
              onMeasureModeChange={handleMeasureModeChange}
              contourSelectMode={contourSelectMode}
              onContourSelectModeChange={handleContourSelectModeChange}
              contourFilterActive={contourFilter != null}
              onImportCoords={() => {
                if (isMapVertexEditSession) {
                  toast({
                    title: "Завершите редактирование контура",
                    description: "Сохраните изменения или нажмите «Отмена» в панели на карте.",
                    variant: "destructive",
                  });
                  return;
                }
                setCoordsImportOpen(true);
              }}
              onCadastreSearch={() => {
                setCadastreSearchOpen((open) => {
                  if (open) return false;
                  setCadastreSearchCollapsed(false);
                  return true;
                });
              }}
              cadastreSearchActive={cadastreSearchOpen}
              onDownload={showMapExportButton ? () => window.dispatchEvent(new CustomEvent("gpml-open-export-panel")) : undefined}
              showWorkItemCreateButton={showWorkItemCreateButton && hasPermission("work_items.create")}
              disabled={activeView !== "map"}
              isSidebarCollapsed={isSidebarCollapsed}
              onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              gridEnabled={oiMapGridPrefs.grid}
              gridStepMeters={oiMapGridPrefs.stepMeters}
              onGridEnabledChange={(grid) =>
                setOiMapGridPrefsPersist({ ...oiMapGridPrefs, grid })
              }
              onGridStepMetersChange={(stepMeters) =>
                setOiMapGridPrefsPersist({ ...oiMapGridPrefs, stepMeters })
              }
              nomenclatureIntersectOpen={oiNomPanelOpen}
              onNomenclatureIntersectToggle={() => {
                const next = !oiNomPanelOpen;
                setOiNomPanelOpen(next);
                const stored = loadOiNomPanelPrefs();
                saveOiNomPanelPrefs({ ...stored, open: next });
                if (!next) handleNomClearHighlight();
              }}
            />

            <GeographicalFilterToolbar>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button
                  type="button"
                  variant={mapSplitView ? "secondary" : "outline"}
                  size="sm"
                  className="h-8 gap-1 text-xs"
                  disabled={activeView !== "map"}
                  onClick={() => setMapSplitView((v) => !v)}
                  title="Split View: карта и таблица объектов"
                >
                  <Table2 className="h-3.5 w-3.5" aria-hidden />
                  Таблица
                </Button>
                {canViewAnalytics() && (
                  <Tabs value={activeView} onValueChange={setActiveView} className="w-[204px]">
                    <TabsList className="grid h-8 w-full grid-cols-2">
                      <TabsTrigger value="map" className="flex items-center justify-center gap-1 text-xs">
                        <MapIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        <span>Карта</span>
                      </TabsTrigger>
                      <TabsTrigger value="analytics" className="flex items-center justify-center gap-1 text-xs">
                        <BarChart3 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        <span>Аналитика</span>
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                )}
              </div>
            </GeographicalFilterToolbar>
          </>
        )}
        workspace={(
          <>
            {activeView === "map" && !isObjectFormOpen && (
              <Suspense fallback={<div className="flex items-center justify-center w-full h-full"><p>Загрузка карты...</p></div>}>
                {(() => {
                  const mapNode = (
                <MapLibreMap
                  selectedObject={selectedObject}
                  geometries={selectedObjectGeometries as Geometry[] || []}
                  focusedGeometryId={focusedGeometryId}
                  geometriesLoading={geometriesLoading}
                  geometriesFetching={geometriesFetching}
                  activeTool={activeTool}
                  measureMode={measureMode}
                  onMeasureDistanceChange={handleMeasureDistanceChange}
                  onMeasurePoint={handleMeasurePoint}
                  onMeasureExit={handleMeasureExit}
                  contourSelectMode={contourSelectMode}
                  onContourSelectComplete={handleContourSelectComplete}
                  onContourSelectExit={handleContourSelectExit}
                  contourPreviewFeature={contourFilter?.feature ?? null}
                  isDrawing={isDrawing}
                  isEditingGeometrySession={isMapVertexEditSession}
                  editingGeometryId={
                    isMapVertexEditSession ? currentDrawing?.geometryId ?? null : null
                  }
                  onDrawingComplete={handleDrawingComplete}
                  onGeometrySelect={handleGeometrySelect}
                  searchQuery={mapSearchQuery}
                  onObjectSelect={(objectId) => {
                    if (objects && Array.isArray(objects)) {
                      const obj = objects.find(o => o && o.id === objectId);
                      if (obj) {
                        handleSelectObject(obj);
                      }
                    }
                  }}
                  onMvtSelect={({ objectId, geometryId, additive }) => {
                    if (additive) {
                      toggleMultiSelectedObject(objectId);
                      return;
                    }
                    clearMultiSelection();
                    setMultiSelectMode(false);
                    if (objects && Array.isArray(objects)) {
                      const obj = objects.find(o => o && o.id === objectId);
                      if (obj) {
                        handleSelectObject(obj, { focusGeometryId: geometryId ?? null });
                      }
                    }
                  }}
                  multiSelectedObjectIds={multiSelectedObjectIds}
                  onMultiSelectObjectIds={(ids) => addMultiSelectedObjectIds(ids)}
                  onFeatureContextMenu={(payload) => {
                    setFeatureContextMenu({
                      objectId: payload.objectId,
                      geometryId: payload.geometryId ?? null,
                      label: payload.label,
                      clientX: payload.clientX,
                      clientY: payload.clientY,
                    });
                  }}
                  cadastralLayersVisible={cadastralLayersVisible}
                  cadastralOpacity={cadastralOpacity}
                  telegramIncidentsVisible={telegramIncidentsVisible}
                  telegramOpacity={telegramOpacity}
                  workItemsVisible={flags.workItemsUiEnabled && workItemsVisible}
                  workItemsOpacity={workItemsOpacity}
                  workItemsPinnedPoints={workItemsPinnedPoints}
                  workItemsRefreshEpoch={workItemsRefreshEpoch}
                  onWorkItemMapClick={handleMapWorkItemPickPoint}
                  mvtTilesEpoch={mvtTilesEpoch}
                  mvtFilters={debouncedMvtFilters}
                  mvtLayerSettings={mvtLayerSettings}
                  showBaseLayerSwitcher={showBaseLayerSwitcher}
                  showMapExportButton={showMapExportButton}
                  useMvt={true}
                  onClearObjectSelection={handleCloseObjectPanel}
                  geometryPrimitiveCode={
                    currentDrawing?.geometry_primitive_code != null
                      ? String(currentDrawing.geometry_primitive_code)
                      : null
                  }
                  pendingSketchFeature={pendingSketchFeature}
                  onMapInstance={setMapInstance}
                />
                  );

                  const measureHud =
                    activeTool === "measure" && measureLabel ? (
                      <div
                        className={cn(
                          "pointer-events-none absolute z-[210] max-w-[16rem]",
                          "rounded-md border border-slate-700/80 bg-slate-800/95 px-2.5 py-1.5",
                          "text-[11px] leading-snug text-white shadow-md whitespace-pre-line tabular-nums",
                          !measureHudCursor && "left-1/2 top-3 -translate-x-1/2",
                        )}
                        style={
                          measureHudCursor
                            ? {
                                left: measureHudCursor.x + 14,
                                top: Math.max(8, measureHudCursor.y - 8),
                              }
                            : undefined
                        }
                        role="status"
                      >
                        {measureLabel}
                      </div>
                    ) : null;

                  const mapWithHud = (
                    <div className="relative h-full w-full">
                      {mapNode}
                      {measureHud}
                    </div>
                  );

                  if (!mapSplitView) {
                    return (
                      <div className="absolute inset-0">
                        {mapWithHud}
                      </div>
                    );
                  }

                  return (
                    <div className="absolute inset-0">
                      <ResizablePanelGroup direction="vertical" className="h-full">
                        <ResizablePanel defaultSize={65} minSize={35} className="min-h-0">
                          {mapWithHud}
                        </ResizablePanel>
                        <ResizableHandle withHandle />
                        <ResizablePanel defaultSize={35} minSize={18} className="min-h-0 bg-background border-t">
                          <MapObjectsSplitTable
                            objects={Array.isArray(objects) ? objects : []}
                            selectedObjectId={selectedObject?.id ?? null}
                            multiSelectedObjectIds={multiSelectedObjectIds}
                            onRowClick={(obj, additive) => {
                              if (additive) {
                                toggleMultiSelectedObject(obj.id);
                                return;
                              }
                              clearMultiSelection();
                              setMultiSelectMode(false);
                              handleSelectObject(obj, { openSidebarDetails: false });
                            }}
                          />
                        </ResizablePanel>
                      </ResizablePanelGroup>
                    </div>
                  );
                })()}
                {activeTool === "measure" && measureMode === "point" && measurePoint ? (
                  <div
                    className={cn(
                      "absolute bottom-4 left-1/2 z-[210] w-[min(22rem,calc(100%-2rem))] -translate-x-1/2",
                      "rounded-md border border-border bg-background/95 p-3 shadow-md",
                    )}
                  >
                    <p className="text-xs font-medium text-foreground">Система координат: WGS-84</p>
                    <p className="mt-1 font-mono text-sm tabular-nums">{measurePoint.text}</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">широта, долгота</p>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="mt-2 h-7 gap-1.5"
                      onClick={() => void copyMeasureCoords()}
                    >
                      <Copy className="h-3.5 w-3.5" aria-hidden />
                      Скопировать
                    </Button>
                  </div>
                ) : null}
                <OiNomenclatureIntersectPanel
                  open={oiNomPanelOpen}
                  onOpenChange={(open) => {
                    setOiNomPanelOpen(open);
                    const stored = loadOiNomPanelPrefs();
                    saveOiNomPanelPrefs({ ...stored, open });
                    if (!open) handleNomClearHighlight();
                  }}
                  onHighlight={handleNomHighlight}
                  onClearHighlight={handleNomClearHighlight}
                  onLocateObject={(id) => void handleNomLocateObject(id)}
                />
                <OiMapRoutingBridge
                  map={mapInstance}
                  active={activeTool === "route"}
                  onClose={handleRoutingClose}
                />
                <MapFeatureContextMenu
                  target={featureContextMenu}
                  onOpenChange={(open) => {
                    if (!open) setFeatureContextMenu(null);
                  }}
                  onSelect={() => {
                    const t = featureContextMenu;
                    setFeatureContextMenu(null);
                    if (!t || !Array.isArray(objects)) return;
                    const obj = objects.find((o) => o && o.id === t.objectId);
                    if (obj) {
                      handleSelectObject(obj, {
                        focusGeometryId: t.geometryId ?? null,
                        openSidebarDetails: false,
                      });
                    }
                  }}
                  onOpenDetails={() => {
                    const t = featureContextMenu;
                    setFeatureContextMenu(null);
                    if (!t || !Array.isArray(objects)) return;
                    const obj = objects.find((o) => o && o.id === t.objectId);
                    if (obj) {
                      handleSelectObject(obj, {
                        focusGeometryId: t.geometryId ?? null,
                        openSidebarDetails: true,
                      });
                    }
                  }}
                  onZoomTo={() => {
                    const t = featureContextMenu;
                    setFeatureContextMenu(null);
                    if (!t || !Array.isArray(objects)) return;
                    const obj = objects.find((o) => o && o.id === t.objectId);
                    if (obj) {
                      handleSelectObject(obj, {
                        focusGeometryId: t.geometryId ?? null,
                        openSidebarDetails: false,
                      });
                    }
                  }}
                  onAddToMultiSelect={() => {
                    const t = featureContextMenu;
                    setFeatureContextMenu(null);
                    if (!t) return;
                    toggleMultiSelectedObject(t.objectId);
                  }}
                />
              </Suspense>
            )}

            {activeView === "analytics" && canViewAnalytics() && (
              <div className="absolute inset-0 z-40 select-text overflow-hidden">
                <Analytics />
              </div>
            )}

            {isDrawing && activeView === "map" && (
              <>
                <MapDrawingModeBanner
                  mode={isMapVertexEditSession ? "edit" : "create"}
                  geometryName={currentDrawing?.name}
                  showVertexShortcut={isMapVertexEditSession}
                  dirty={drawingDirty}
                />
                <DrawingPanel
                  activeTool={activeTool}
                  drawing={currentDrawing}
                  objectId={currentObjectId}
                  onSave={handleDrawingSave}
                  onCancel={handleDrawingCancel}
                  onDirtyChange={setDrawingDirty}
                  mapFirstMode={mapFirstGeometryFlow && currentObjectId == null}
                  onMapFirstCommit={handleMapFirstCommit}
                  mapFirstSnapshotRef={mapFirstSnapshotRef}
                  onCreateFromOsm={() => {
                    const ring = extractSelectionPolygonRing(
                      currentDrawing as Record<string, unknown> | null,
                    );
                    if (!ring) {
                      toast({
                        title: "Нужен полигон",
                        description: "Нарисуйте полигон выделения для импорта из OSM",
                        variant: "destructive",
                      });
                      return;
                    }
                    setOsmImportCoords(ring);
                    setOsmImportOpen(true);
                  }}
                />
              </>
            )}
            {osmReviewBatchId && activeView === "map" && (
              <div className={cn("absolute bottom-0 right-0 top-14 flex", MAP_PANEL_Z.sideOverlay)}>
                <OsmReviewPanel
                  batchId={osmReviewBatchId}
                  onClose={() => setOsmReviewBatchId(null)}
                  onPublished={() => {
                    setOsmReviewBatchId(null);
                    setMvtTilesEpoch((e) => e + 1);
                    queryClient.invalidateQueries({ queryKey: ["objects"] });
                  }}
                />
              </div>
            )}
            {cadastreSearchOpen && activeView === "map" && (
              <div className={cn("absolute bottom-0 right-0 top-14 flex", MAP_PANEL_Z.sideOverlay)}>
                <CadastreSearchPanel
                  collapsed={cadastreSearchCollapsed}
                  onCollapsedChange={setCadastreSearchCollapsed}
                  onClose={() => {
                    setCadastreSearchOpen(false);
                    setCadastreSearchCollapsed(false);
                  }}
                />
              </div>
            )}
          </>
        )}
      />

      <OsmImportDialog
        open={osmImportOpen}
        onOpenChange={setOsmImportOpen}
        coordinates={osmImportCoords}
        onImported={(res) => {
          setOsmReviewBatchId(res.batch_id);
          setIsDrawing(false);
          setActiveTool(null);
          setCurrentDrawing(null);
        }}
      />

      <GeometryCoordsImportDialog
        open={coordsImportOpen}
        onOpenChange={setCoordsImportOpen}
        onImport={handleCoordsImportSuccess}
      />

      <Dialog
        open={isObjectFormOpen}
        onOpenChange={(open) => {
          setIsObjectFormOpen(open);
          if (!open) setInitialGeometryForObjectForm(null);
        }}
      >
        <ObjectForm
          objectTypes={objectTypes || []}
          organizations={organizations || []}
          onCancel={() => setIsObjectFormOpen(false)}
          onCreateSuccess={handleObjectCreateSuccessForPendingGeometry}
          initialGeometryFromMapFirst={initialGeometryForObjectForm}
        />
      </Dialog>

      <Dialog open={isEditObjectFormOpen} onOpenChange={setIsEditObjectFormOpen}>
        <ObjectForm
          objectTypes={objectTypes || []}
          organizations={organizations || []}
          onCancel={() => {
            setIsEditObjectFormOpen(false);
            setObjectToEdit(null);
          }}
          editObject={objectToEdit ? {
            id: objectToEdit.id,
            name: objectToEdit.name,
            fullName: objectToEdit.full_name,
            kls_id: objectToEdit.kls_id,
            organizationId: objectToEdit.organization_id,
            status: objectToEdit.status,
            description: objectToEdit.description,
            address: objectToEdit.address,
            area: objectToEdit.area,
          } : undefined}
        />
      </Dialog>

      <Dialog
        open={isGeometryTypeDialogOpen}
        onOpenChange={(open) => {
          if (!open) clearPendingCoordsImport();
          setIsGeometryTypeDialogOpen(open);
        }}
      >
        <DialogContent onCloseAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Выбор типа геометрии</DialogTitle>
            <DialogDescription>
              {pendingImportFeatureCount != null
                ? "Перед применением импортированного контура выберите семантический тип геометрии и тип геометрического примитива."
                : "Перед началом рисования выберите семантический тип геометрии и тип геометрического примитива."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Семантический тип (KLS qual=345)</Label>
              <Combobox
                options={geometrySemanticTypes.map((item) => ({
                  value: String(item.kls_id),
                  label: item.kls_names || item.kls_namef,
                  fullLabel: item.kls_namef,
                }))}
                value={selectedGeometrySemanticKlsId}
                onValueChange={onSemanticKlsChange}
                placeholder="Выберите семантический тип"
                searchPlaceholder="Поиск семантического типа..."
                emptyText="Семантические типы не найдены"
                allowClear={false}
              />
            </div>
            <div className="space-y-2">
              <Label>Тип примитива (KLS qual=343)</Label>
              <Combobox
                options={geometryPrimitiveOptions.map((item) => ({
                  value: String(item.kls_id),
                  label: item.kls_names || item.kls_namef,
                  fullLabel: item.kls_namef,
                }))}
                value={selectedGeometryPrimitiveKlsId}
                onValueChange={onPrimitiveKlsChange}
                placeholder="Выберите тип примитива"
                searchPlaceholder="Поиск типа примитива..."
                emptyText="Типы примитивов не найдены"
                allowClear={false}
              />
              {primitiveOptionsForSemantic?.restricted === true && (
                <p className="text-xs text-muted-foreground">
                  Список ограничен связями KLS (семантика → примитив) для выбранного типа.
                </p>
              )}
            </div>
            <div className="space-y-2 rounded-md border p-3">
              <Label>Шаблон полей геометрии</Label>
              {selectedGeometrySemanticKlsId ? (
                geometryTemplateFieldsPreview.length > 0 ? (
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <p>Будут доступны поля ({geometryTemplateFieldsPreview.length}):</p>
                    <ul className="list-disc space-y-1 pl-5">
                      {geometryTemplateFieldsPreview.slice(0, 8).map((field) => (
                        <li key={field.id}>
                          {field.label}
                          {field.is_required ? " *" : ""}
                        </li>
                      ))}
                      {geometryTemplateFieldsPreview.length > 8 && (
                        <li>... и еще {geometryTemplateFieldsPreview.length - 8}</li>
                      )}
                    </ul>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Для выбранного семантического типа геометрии отдельные поля шаблона пока не настроены.
                  </p>
                )
              ) : (
                <p className="text-xs text-muted-foreground">
                  Выберите семантический тип, чтобы увидеть поля карточки геометрии.
                </p>
              )}
            </div>
            <div className="space-y-2 rounded-md border p-3">
              <Label>Подсказки по ограничениям</Label>
              <div className="space-y-1 text-xs text-muted-foreground">
                {selectedSemanticType?.kls_note ? (
                  <p>{selectedSemanticType.kls_note}</p>
                ) : (
                  <p>Бизнес-ограничения для выбранного типа можно задать в описании KLS (`kls_note`).</p>
                )}
                {spatialHintsFromTemplate.length > 0 && (
                  <div className="space-y-1">
                    <p className="font-medium text-foreground">Пространственные правила (шаблон)</p>
                    <ul className="list-disc space-y-1 pl-5">
                      {spatialHintsFromTemplate.map((hint, idx) => (
                        <li key={`sp-${idx}`}>{hint}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {geometryConstraintHints.length > 0 ? (
                  <ul className="list-disc space-y-1 pl-5">
                    {geometryConstraintHints.slice(0, 6).map((hint, idx) => (
                      <li key={`${hint}-${idx}`}>{hint}</li>
                    ))}
                    {geometryConstraintHints.length > 6 && <li>... и еще {geometryConstraintHints.length - 6}</li>}
                  </ul>
                ) : (
                  spatialHintsFromTemplate.length === 0 && (
                    <p>Формальные ограничения полей пока не обнаружены в шаблоне.</p>
                  )
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                clearPendingCoordsImport();
                setIsGeometryTypeDialogOpen(false);
              }}
            >
              Отмена
            </Button>
            <Button
              type="button"
              onClick={() => {
                window.setTimeout(() => {
                  confirmGeometryTypesRef.current();
                }, 0);
              }}
            >
              Начать рисование
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isGeometryAnalyzeDialogOpen}
        onOpenChange={(open) => {
          setIsGeometryAnalyzeDialogOpen(open);
          if (!open) setGeometryAnalyzeResult(null);
        }}
      >
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Куда добавить геометрию?</DialogTitle>
            <DialogDescription>
              {geometryAnalyzeResult && geometryAnalyzeResult.candidates.length > 0 ? (
                <>
                  По контуру найдены объекты в вашей области доступа. Нажмите «Выбрать» у нужного объекта, затем
                  «Сохранить изменения» на панели рисования. Либо «Создать новый объект» — контур будет добавлен вместе
                  с сохранением карточки (или через классическую форму, если шаблона нет).
                </>
              ) : (
                <>
                  Пересечений с существующими объектами в вашей области доступа не найдено. Создайте новый объект через
                  кнопку ниже — нарисованный контур прикрепится при сохранении формы (при наличии шаблона карточки или
                  через классическое создание объекта).
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {geometryAnalyzeResult?.multiple_organizations ? (
            <Alert>
              <AlertDescription>
                Объекты из разных организаций — уточните целевой объект; добавление к чужой организации может быть
                недоступно по правам.
              </AlertDescription>
            </Alert>
          ) : null}
          {mapFirstAnalyzeSuggested ? (
            <Alert className="border-primary/40 bg-primary/5">
              <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-sm">
                  Рекомендуемый объект: «{mapFirstAnalyzeSuggested.candidate.object_name}»
                  {mapFirstAnalyzeSuggested.reason === "ratio_above_threshold"
                    ? " (высокая доля пересечения с контуром)"
                    : null}
                </span>
                <Button
                  type="button"
                  size="sm"
                  className="shrink-0"
                  disabled={analyzeLoading}
                  onClick={() =>
                    applyAnalyzedObjectFromMapFirst(mapFirstAnalyzeSuggested.candidate.object_id)
                  }
                >
                  Выбрать рекомендуемый
                </Button>
              </AlertDescription>
            </Alert>
          ) : null}
          <div className="space-y-2">
            {geometryAnalyzeResult?.candidates.map((c) => (
              <div
                key={`${c.object_id}-${c.geometry_id}`}
                className="flex items-center justify-between gap-2 rounded-md border p-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate" title={c.object_name}>
                    {c.object_name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {c.relation}
                    {c.intersection_ratio != null
                      ? ` · ${Math.round(c.intersection_ratio * 100)}% от площади контура`
                      : ""}
                    {!c.can_attach_geometry ? " · нет права редактирования" : ""}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  disabled={!c.can_attach_geometry || analyzeLoading}
                  onClick={() => applyAnalyzedObjectFromMapFirst(c.object_id)}
                >
                  Выбрать
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsGeometryAnalyzeDialogOpen(false);
                setGeometryAnalyzeResult(null);
              }}
            >
              Закрыть
            </Button>
            <Button
              type="button"
              onClick={() => {
                const payload = buildInitialGeometryPayloadForWithCard(
                  currentDrawingRef.current,
                  activeTool,
                  mapFirstSnapshotRef.current,
                );
                if (!payload) {
                  toast({
                    title: "Недостаточно данных контура",
                    description: "Убедитесь, что контур завершён и выбраны типы геометрии (KLS).",
                    variant: "destructive",
                  });
                  return;
                }
                setInitialGeometryForObjectForm(payload);
                setIsGeometryAnalyzeDialogOpen(false);
                setGeometryAnalyzeResult(null);
                setIsObjectFormOpen(true);
              }}
            >
              Создать новый объект
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isMapWorkItemDialogOpen}
        onOpenChange={(open) => {
          setIsMapWorkItemDialogOpen(open);
          if (!open) {
            setMapWorkItemAnchor(null);
            setMapWorkItemAutoLink(null);
            resetMapWorkItemDraft();
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Создать задачу в точке</DialogTitle>
            <DialogDescription>
              {mapWorkItemAnchor
                ? `Координаты: ${mapWorkItemAnchor.lat.toFixed(6)}, ${mapWorkItemAnchor.lng.toFixed(6)}`
                : "Кликните по карте, чтобы выбрать точку."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Тип</Label>
              <Select
                value={mapWorkItemDraft.kind}
                onValueChange={(value) => setMapWorkItemDraft((p) => ({ ...p, kind: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Выберите тип" />
                </SelectTrigger>
                <SelectContent className="z-[250]">
                  {mapWorkItemKindOptions.map((kind) => (
                    <SelectItem key={kind.code} value={kind.code}>
                      {kind.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="map-work-item-title">Заголовок</Label>
              <Input
                id="map-work-item-title"
                value={mapWorkItemDraft.title}
                onChange={(e) => setMapWorkItemDraft((p) => ({ ...p, title: e.target.value }))}
                placeholder="Например: Проверить объект на месте"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="map-work-item-description">Описание</Label>
              <Textarea
                id="map-work-item-description"
                rows={3}
                value={mapWorkItemDraft.description}
                onChange={(e) => setMapWorkItemDraft((p) => ({ ...p, description: e.target.value }))}
                placeholder="Коротко опишите задачу или инцидент"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="map-work-item-status">Статус</Label>
                <Select
                  value={mapWorkItemDraft.status}
                  onValueChange={(value) => setMapWorkItemDraft((p) => ({ ...p, status: value }))}
                >
                  <SelectTrigger id="map-work-item-status">
                    <SelectValue placeholder="Выберите статус" />
                  </SelectTrigger>
                  <SelectContent className="z-[250]">
                  {mapWorkItemStatusOptions.map((statusOption) => (
                    <SelectItem key={statusOption.code} value={statusOption.code}>
                      {statusOption.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="map-work-item-priority">Приоритет (1-5)</Label>
                <Select
                  value={String(mapWorkItemDraft.priority)}
                  onValueChange={(value) =>
                    setMapWorkItemDraft((p) => ({
                      ...p,
                      priority: Number(value),
                    }))
                  }
                >
                  <SelectTrigger id="map-work-item-priority">
                    <SelectValue placeholder="Выберите приоритет" />
                  </SelectTrigger>
                  <SelectContent className="z-[250]">
                    {MAP_WORK_ITEM_PRIORITY_OPTIONS.map((priorityValue) => (
                      <SelectItem key={priorityValue} value={String(priorityValue)}>
                        {priorityValue}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="map-work-item-assignee">Исполнитель (опционально)</Label>
              <Combobox
                options={mapWorkItemAssigneeOptions}
                value={mapWorkItemDraft.assignedToUserId || undefined}
                onValueChange={(value) =>
                  setMapWorkItemDraft((p) => ({ ...p, assignedToUserId: value ?? "" }))
                }
                placeholder="Выберите исполнителя"
                searchPlaceholder="Поиск исполнителя..."
                emptyText="Пользователи не найдены"
              />
            </div>

            <Alert>
              <AlertDescription>
                {mapWorkItemAutoLink?.objectId
                  ? `Точка попала в объект «${mapWorkItemAutoLink.objectName ?? mapWorkItemAutoLink.objectId}», привязка будет установлена автоматически.`
                  : "Точка вне геометрий объектов в вашей области доступа — запись будет создана без привязки."}
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsMapWorkItemDialogOpen(false);
                setMapWorkItemAnchor(null);
                setMapWorkItemAutoLink(null);
                resetMapWorkItemDraft();
              }}
              disabled={mapWorkItemSubmitting}
            >
              Отмена
            </Button>
            <Button type="button" onClick={() => void submitMapWorkItem()} disabled={mapWorkItemSubmitting}>
              {mapWorkItemSubmitting ? "Сохранение..." : "Создать"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={cardLeaveDialog != null}
        onOpenChange={(open) => {
          if (!open) setCardLeaveDialog(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Несохранённые изменения карточки</AlertDialogTitle>
            <AlertDialogDescription>
              Есть несохранённые правки карточки. Продолжить и потерять изменения?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Вернуться</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const run = cardLeaveDialog?.run;
                setCardLeaveDialog(null);
                setCardDirty(false);
                run?.();
              }}
            >
              Продолжить без сохранения
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MapUiContext.Provider>
  );
}
